import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ScrollView,
  TextInput,
  Platform,
  Alert,
  Dimensions,
  Animated,
  Modal,
  useWindowDimensions,
  ActivityIndicator,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image } from 'expo-image';
import { WebView } from 'react-native-webview';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import CourseMediaCarousel, {
  createCourseMediaCarouselStyles,
  resolveRemoteUri,
} from '../components/CourseMediaCarousel';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import StudentAvatar from '../components/StudentAvatar';
import Stopwatch from '../components/Stopwatch';
import { fetchExerciseRoutines, fetchSharedExerciseRoutines } from '../utils/exerciseRoutinesApi';
import { getApiUrl } from '../utils/apiConfig';
import {
  exercisesListUrl,
  exerciseByIdUrl,
  exerciseTypesByCourseUrl,
  nonCourseExerciseTypesUrl,
} from '../utils/exerciseApi';
import { dedupeCoursesForDropdown } from '../utils/coursesCatalog';
import { filterCoursesForExerciseCreation } from '../utils/exerciseCreationAccess';
import {
  findDefaultNextGrade,
  isGradeBeyondViewerAccess,
} from '../utils/gradeAccess';
import { normalizeStudentUserRecord } from '../utils/sessionUser';
import { hydrateExercisesFromSessionNotes } from '../utils/trainingSessionNotes';
import { useUserPreferences } from '../context/UserPreferencesContext';
import {
  displayDateToIso,
  formatDateLabelFromIso,
  dateInputPlaceholder,
  isoDateToDisplay,
} from '../utils/dateFormat';
import SessionDateCalendarModal from '../components/SessionDateCalendarModal';
import TrainingSessionTimelineView from '../components/TrainingSessionTimelineView';
import TrainingCalendarDayIcon from '../components/TrainingCalendarDayIcon';
import { parseAssignedCourses } from '../utils/trainingCalendarFilters';
import SessionExerciseSetsEditor from '../components/SessionExerciseSetsEditor';
import {
  attachPerformanceToExercise,
  attachExerciseWithWeightDefaults,
  mapPerformanceSetsFromApi,
  performanceSetsToPayload,
  getSetDisplaySeconds,
  getSetDisplayWeight,
  syncExercisePrescribedCounts,
  createDefaultPerformanceSets,
  clampInt,
  isTimeBasedExercise,
  isHeightBasedExercise,
  isWeightBasedExercise,
} from '../utils/sessionExercisePerformance';

const { width } = Dimensions.get('window');

// Local assets
const COURSES_IMAGE = require('../../assets/ChatGPT Image Jan 1, 2026, 02_28_17 AM.png');
const ROUTINES_IMAGE = require('../../assets/_IXvICAYM4KkeCcxwaZQk_MPzCWQBp.png');
const EXERCISES_IMAGE = require('../../assets/ChatGPT Image Jan 20, 2026, 08_11_59 AM.png');
const GOALS_IMAGE = require('../../assets/ChatGPT Image Mar 13, 2026, 08_37_09 PM.png');
const PLACEHOLDER_IMAGE = require('../../assets/ChatGPT Image Jan 1, 2026, 02_28_17 AM.png');

const SESSION_TILE_CAROUSEL_BREAKPOINT = 768;
const SESSION_BUILDER_TILE_COUNT = 4;
const SESSION_SAVE_SUCCESS_MESSAGE = 'the training session was successfully logged';
const SESSION_SAVE_ERROR_MESSAGE = 'there was an issue saving your session, please try again';

const EXERCISE_DETAIL_MODAL_CAROUSEL_ASPECT = 16 / 9;

function getSessionDurationMinutes(durationMinutes) {
  if (!durationMinutes) return null;
  const parsed = parseInt(durationMinutes, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function buildSessionExercisePayload(exercise, exerciseOrder) {
  const synced = syncExercisePrescribedCounts(exercise);
  const parsedTime =
    synced.time != null && synced.time !== ''
      ? clampInt(synced.time, 0)
      : null;
  const parsedWeight =
    synced.weight_kg != null && synced.weight_kg !== '' && !Number.isNaN(Number(synced.weight_kg))
      ? Number(synced.weight_kg)
      : null;
  return {
    exercise_id: exercise.exercise_id,
    exercise_name: exercise.exercise_name,
    exercise_type: exercise.exercise_type,
    exercise_order: exerciseOrder,
    sets: synced.sets,
    reps: synced.reps,
    weight_kg: parsedWeight,
    height: exercise.height,
    tempo: exercise.tempo,
    is_time_based_exercise: isTimeBasedExercise(synced),
    is_height_based: isHeightBasedExercise(synced),
    is_weight_based: isWeightBasedExercise(synced),
    time: isTimeBasedExercise(synced) ? parsedTime : null,
    performance_sets: performanceSetsToPayload(synced.performance_sets, synced),
  };
}

async function readSessionExerciseApiError(response, fallback) {
  try {
    const body = await response.json();
    return body.detail || body.error || fallback;
  } catch {
    return fallback;
  }
}

function formatExerciseSetsRepsSummary(exercise, weightInKg = true) {
  const synced = syncExercisePrescribedCounts(exercise);
  const setCount = synced.performance_sets?.length || synced.sets || 1;
  const parts = [`${setCount} sets`];
  if (isTimeBasedExercise(exercise)) {
    const firstSet = synced.performance_sets?.[0];
    const seconds = firstSet
      ? getSetDisplaySeconds(firstSet, exercise)
      : clampInt(exercise.time ?? synced.reps ?? 0, 0);
    parts.push(`${seconds}s`);
  } else {
    const repCounts = (synced.performance_sets || []).map((setRow) =>
      clampInt(setRow.prescribed_reps ?? synced.reps ?? 1)
    );
    const uniqueReps = [...new Set(repCounts)];
    const repsText =
      uniqueReps.length > 1
        ? `${Math.min(...uniqueReps)}–${Math.max(...uniqueReps)} reps`
        : `${uniqueReps[0] ?? synced.reps ?? 1} reps`;
    parts.push(repsText);
  }
  if (isHeightBasedExercise(exercise)) {
    parts.push('height');
  }
  if (isWeightBasedExercise(exercise)) {
    const firstSet = synced.performance_sets?.[0];
    const w = firstSet ? getSetDisplayWeight(firstSet, weightInKg) : null;
    if (w) parts.push(`${w} ${weightInKg ? 'kg' : 'lbs'}`);
  }
  return parts.join(' · ');
}

function buildSessionSaveSignature({ currentUserId, sessionDate, durationMinutes, notes, sessionExercises }) {
  return JSON.stringify({
    student_id: currentUserId,
    session_date: sessionDate,
    duration_minutes: getSessionDurationMinutes(durationMinutes),
    notes: notes || null,
    exercises: sessionExercises.map((exercise, index) =>
      buildSessionExercisePayload(exercise, index)
    ),
  });
}

function localCalendarIsoDate(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayIsoDate() {
  return localCalendarIsoDate(new Date());
}

function addDaysToIsoDate(isoDate, dayDelta) {
  const d = new Date(`${normalizeSessionDateInput(isoDate)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  d.setDate(d.getDate() + dayDelta);
  return localCalendarIsoDate(d);
}

function normalizeSessionDateInput(value, isUS = false) {
  const s = String(value || '').trim();
  if (!s) return todayIsoDate();
  const parsed = displayDateToIso(s, isUS);
  if (parsed) return parsed;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s.slice(0, 10);
  return s.slice(0, 10);
}

function isCompleteSessionDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(normalizeSessionDateInput(value));
}

function isSessionDateToday(sessionDate) {
  return normalizeSessionDateInput(sessionDate) === todayIsoDate();
}

function formatExistingSessionDateLabel(sessionDate, isUS = false) {
  if (isSessionDateToday(sessionDate)) return 'Today';
  return formatDateLabelFromIso(normalizeSessionDateInput(sessionDate, isUS), isUS);
}

function normalizeDbSessionDate(sessionDate) {
  if (!sessionDate) return '';
  const raw = String(sessionDate).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = sessionDate instanceof Date ? sessionDate : new Date(sessionDate);
  if (!Number.isNaN(d.getTime())) return localCalendarIsoDate(d);
  return raw.slice(0, 10);
}

async function fetchTrainingSessionsForCalendarDate(studentId, dateKey) {
  const res = await fetch(
    `${getApiUrl()}/api/training-sessions?student_id=${encodeURIComponent(String(studentId))}&session_date=${encodeURIComponent(dateKey)}`
  );
  if (!res.ok) return [];
  const rows = await res.json();
  return Array.isArray(rows) ? rows : [];
}

/** All sessions for an exact calendar day (adjacent-day fetch handles timezone drift). */
async function findAllTrainingSessionsForStudentDate(studentId, dateKey) {
  const keys = [dateKey, addDaysToIsoDate(dateKey, -1), addDaysToIsoDate(dateKey, 1)];
  const byId = new Map();
  for (const key of keys) {
    const rows = await fetchTrainingSessionsForCalendarDate(studentId, key);
    for (const row of rows) {
      if (row?.id != null) byId.set(row.id, row);
    }
  }
  return [...byId.values()].filter(
    (row) => normalizeDbSessionDate(row.session_date) === dateKey
  );
}

function sortSessionsForPicker(rows) {
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const countA = Number(a.exercise_count) || 0;
    const countB = Number(b.exercise_count) || 0;
    if (countB !== countA) return countB - countA;
    return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
  });
}

function mapDbTrainingExerciseToSessionState(row) {
  const typeName =
    row.catalog_exercise_type_name || row.exercise_type || 'Exercise';
  const typeId =
    row.catalog_exercise_type_id != null
      ? row.catalog_exercise_type_id
      : `loaded-${typeName}`;
  const rowId =
    row.id != null
      ? `tse-${row.id}`
      : `legacy-${row.training_session_id || 'session'}-${typeName}`;
  const base = {
    id: rowId,
    training_session_exercise_id: row.id ?? null,
    exercise_id: row.exercise_id,
    exercise_name: row.exercise_name,
    exercise_type: typeName,
    exercise_type_id: typeId,
    sets: row.sets ?? 1,
    reps: row.reps ?? 1,
    weight_kg: row.weight_kg ?? null,
    height: row.height ?? null,
    tempo: row.tempo ?? null,
    is_time_based_exercise: row.is_time_based_exercise,
    is_height_based: row.is_height_based,
    is_weight_based: row.is_weight_based,
    time: row.time ?? null,
    display_url: row.display_url ?? null,
    display_images: row.display_images ?? null,
    description: row.description ?? null,
    performance_sets:
      mapPerformanceSetsFromApi(row.performance_sets, row) ||
      createDefaultPerformanceSets(row.sets ?? 1, row.reps ?? 1, row),
  };
  return attachPerformanceToExercise(base);
}

function buildSessionExerciseTypesFromRows(rows) {
  const sorted = [...rows].sort((a, b) => {
    const oa = a.exercise_order ?? 0;
    const ob = b.exercise_order ?? 0;
    if (oa !== ob) return oa - ob;
    return (a.id ?? 0) - (b.id ?? 0);
  });
  const byType = new Map();
  sorted.forEach((row) => {
    const mapped = mapDbTrainingExerciseToSessionState(row);
    const key = String(mapped.exercise_type_id);
    if (!byType.has(key)) {
      byType.set(key, {
        id: `type-loaded-${key}`,
        exercise_type_id: mapped.exercise_type_id,
        exercise_type_name: mapped.exercise_type,
        exercises: [],
      });
    }
    byType.get(key).exercises.push(mapped);
  });
  return Array.from(byType.values());
}

const EXISTING_SESSION_HELP_TEXT =
  '(not adding it to an existing session for the given day just means a separate training session is logged for that day).';

function getExistingSessionPromptText(sessionDate, isUS = false, multiple = false) {
  const label = formatExistingSessionDateLabel(sessionDate, isUS);
  return multiple
    ? `Do you want to add this to an existing Training session for ${label}?`
    : `Do you want to add this to your existing Training session for ${label}?`;
}

function getDuplicateSessionPromptText(sessionDate, isUS = false) {
  const label = formatExistingSessionDateLabel(sessionDate, isUS);
  return `This session has already been logged for ${label}. Are you sure you want to log this again for this day?`;
}

function collectTimelineExercisesForRemoval(sessionExerciseTypes, idsToRemove) {
  const idSet = new Set([...idsToRemove].map((id) => String(id)));
  const found = [];
  for (const exerciseType of sessionExerciseTypes) {
    for (const exercise of exerciseType.exercises) {
      if (idSet.has(String(exercise.id))) {
        found.push(exercise);
      }
    }
  }
  return found;
}

function normalizeExerciseIdentityForComparison(exercise, orderIndex) {
  const synced = syncExercisePrescribedCounts(exercise);
  return {
    exercise_id: exercise.exercise_id ?? null,
    exercise_name: String(exercise.exercise_name || '').trim().toLowerCase(),
    exercise_type: String(exercise.exercise_type || '').trim().toLowerCase(),
    order: Number(exercise.exercise_order != null ? exercise.exercise_order : orderIndex),
    sets: Number(synced.sets) || 0,
    reps: Number(synced.reps) || 0,
    is_time_based: !!isTimeBasedExercise(synced),
    is_height_based: !!isHeightBasedExercise(synced),
    time: synced.time ?? null,
  };
}

function buildSessionExerciseContentFingerprint(exercises) {
  const list = Array.isArray(exercises) ? exercises : [];
  const items = list.map((exercise, index) =>
    normalizeExerciseIdentityForComparison(exercise, index)
  );
  items.sort((a, b) => a.order - b.order);
  return JSON.stringify(items);
}

function buildSessionExerciseContentFingerprintFromDbRows(rows) {
  const sorted = [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const oa = a.exercise_order ?? 0;
    const ob = b.exercise_order ?? 0;
    if (oa !== ob) return oa - ob;
    return (a.id ?? 0) - (b.id ?? 0);
  });
  return buildSessionExerciseContentFingerprint(
    sorted.map((row, index) => {
      const mapped = mapDbTrainingExerciseToSessionState(row);
      return { ...mapped, exercise_order: row.exercise_order ?? index };
    })
  );
}

async function fetchTrainingSessionExerciseRows(sessionId) {
  const exRes = await fetch(
    `${getApiUrl()}/api/training-session-exercises?training_session_id=${encodeURIComponent(String(sessionId))}`
  );
  if (!exRes.ok) return [];
  const rows = await exRes.json();
  return Array.isArray(rows) ? rows : [];
}

async function findDuplicateTrainingSessionForExercises(studentId, dateKey, sessionExercises) {
  if (!Array.isArray(sessionExercises) || sessionExercises.length === 0) return null;
  const targetFingerprint = buildSessionExerciseContentFingerprint(sessionExercises);
  const sessions = await findAllTrainingSessionsForStudentDate(studentId, dateKey);
  for (const session of sessions) {
    let rows = await fetchTrainingSessionExerciseRows(session.id);
    rows = await hydrateExercisesFromSessionNotes(rows, session.notes, session.id);
    const dbFingerprint = buildSessionExerciseContentFingerprintFromDbRows(rows);
    if (dbFingerprint === targetFingerprint) {
      return session;
    }
  }
  return null;
}

/** CSV URLs from `display_images` plus optional non-YouTube `display_url` (full https URLs from exerciseImageBucket). */
function parseExerciseGalleryMediaUrls(exercise, isYouTubeUrlFn) {
  if (!exercise || typeof exercise !== 'object') return [];
  const out = [];
  const seen = new Set();
  const push = (u) => {
    const t = String(u ?? '').trim();
    if (!t || seen.has(t)) return;
    if (!/^https?:\/\//i.test(t)) return;
    seen.add(t);
    out.push(t);
  };
  const csv = exercise.display_images;
  if (typeof csv === 'string' && csv.trim()) {
    csv.split(',').forEach((part) => push(part.trim()));
  }
  const du = exercise.display_url;
  if (du && typeof du === 'string' && du.trim()) {
    const t = du.trim();
    if (!isYouTubeUrlFn(t)) push(t);
  }
  return out;
}

/** Stable module-scope check so gallery parsing matches modal video branch intent. */
function exerciseGalleryIsYouTubeUrl(url) {
  if (!url) return false;
  const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
  return youtubeRegex.test(url);
}

export default function SessionBuilder({ navigation, route }) {
  const theme = useTheme();
  const { isUSUser, weightInKg } = useUserPreferences();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [currentUser, setCurrentUser] = useState(null);
  const stopwatchRef = useRef(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [routines, setRoutines] = useState([]);
  const [sharedRoutines, setSharedRoutines] = useState([]);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [exerciseTypes, setExerciseTypes] = useState([]);
  const [selectedExerciseType, setSelectedExerciseType] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [sessionExercises, setSessionExercises] = useState([]);
  const [sessionExerciseTypes, setSessionExerciseTypes] = useState([]); // New: stores exerciseTypes with nested exercises
  const [expandedExerciseTypes, setExpandedExerciseTypes] = useState(new Set()); // Track which exerciseTypes are expanded
  const [expandedSessionExerciseIds, setExpandedSessionExerciseIds] = useState(new Set());
  const [selectedExerciseTypesForRoutine, setSelectedExerciseTypesForRoutine] = useState(new Set()); // Track which exerciseTypes are selected for routine
  const [selectedTimelineExerciseIds, setSelectedTimelineExerciseIds] = useState(new Set());
  const [removingTimelineExercises, setRemovingTimelineExercises] = useState(false);
  const [showRoutineModal, setShowRoutineModal] = useState(false);
  const [routineName, setRoutineName] = useState('');
  const [routineDescription, setRoutineDescription] = useState('');
  const [showCoursesDropdown, setShowCoursesDropdown] = useState(false);
  const [showRoutinesDropdown, setShowRoutinesDropdown] = useState(false);
  const [showExerciseTypesDropdown, setShowExerciseTypesDropdown] = useState(false);
  const [showExercisesDropdown, setShowExercisesDropdown] = useState(false);
  const [showExercisesMenu, setShowExercisesMenu] = useState(false);
  const [showGoalsDropdown, setShowGoalsDropdown] = useState(false);
  const [showGradeExercises, setShowGradeExercises] = useState(false);
  /** 'course' | 'assigned' | 'grade' — which flow opened the exercise picker */
  const [exercisePickerSource, setExercisePickerSource] = useState(null);
  const [grades, setGrades] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [showGradesDropdown, setShowGradesDropdown] = useState(false);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [loadingGradeExercises, setLoadingGradeExercises] = useState(false);
  const [goals, setGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [expandedGoalId, setExpandedGoalId] = useState(null);
  const [goalMilestonesByGoalId, setGoalMilestonesByGoalId] = useState({});
  const [loadingGoalMilestonesId, setLoadingGoalMilestonesId] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSessionDateCalendar, setShowSessionDateCalendar] = useState(false);
  const [notes, setNotes] = useState('');
  const [sessionDate, setSessionDate] = useState(todayIsoDate());
  const [existingSessionForDate, setExistingSessionForDate] = useState(null);
  const [existingSessionsForDate, setExistingSessionsForDate] = useState([]);
  const [existingSessionPickerModal, setExistingSessionPickerModal] = useState(null);
  const [pickerHighlightSessionId, setPickerHighlightSessionId] = useState(null);
  const [appendToExistingSession, setAppendToExistingSession] = useState(false);
  const [showExistingSessionHelp, setShowExistingSessionHelp] = useState(false);
  const [loadingExistingSessionTimeline, setLoadingExistingSessionTimeline] = useState(false);
  const sessionScrollRef = useRef(null);
  const [durationMinutes, setDurationMinutes] = useState('');
  const [selectedExercises, setSelectedExercises] = useState(new Set());
  const [selectAllExercises, setSelectAllExercises] = useState(false);
  const [selectedExerciseForDetail, setSelectedExerciseForDetail] = useState(null);
  const [showExerciseDetailModal, setShowExerciseDetailModal] = useState(false);
  const [exerciseCarouselVideoModal, setExerciseCarouselVideoModal] = useState(null);
  /** Larger popup when tapping an exercise still image (carousel or single display_url). */
  const [exerciseImageLightbox, setExerciseImageLightbox] = useState(null);
  /** Display-only rotation for gallery / single image in exercise detail modal (90° steps). */
  const [exerciseDetailGalleryRotationDeg, setExerciseDetailGalleryRotationDeg] = useState(0);
  const [sessionSaveToast, setSessionSaveToast] = useState(null);
  const [duplicateSessionModal, setDuplicateSessionModal] = useState(null);
  const [dateChangeClearModal, setDateChangeClearModal] = useState(null);
  const [savingSession, setSavingSession] = useState(false);
  const lastSavedSessionSignatureRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const pickerSelectingRef = useRef(false);
  const coursesTileColorAnim = useRef(new Animated.Value(1)).current;
  const routinesTileColorAnim = useRef(new Animated.Value(1)).current;
  const exercisesTileColorAnim = useRef(new Animated.Value(1)).current;
  const goalsTileColorAnim = useRef(new Animated.Value(1)).current;
  const sessionTilesCarouselRef = useRef(null);
  const sessionCarouselIndexRef = useRef(0);

  const isSessionTilesCarousel = windowWidth < SESSION_TILE_CAROUSEL_BREAKPOINT;
  const [sessionCarouselIndex, setSessionCarouselIndex] = useState(0);
  const [sessionTilesViewportW, setSessionTilesViewportW] = useState(0);

  const enrolledCourses = useMemo(
    () => filterCoursesForExerciseCreation(currentUser, courses),
    [currentUser, courses]
  );

  useEffect(() => {
    // Get current user from route params or fetch from API
    const userId = route?.params?.userId || 1; // Default to 1 for testing
    fetchUser(userId);
    fetchCourses();
    if (userId) {
      fetchRoutines(userId);
    }
  }, []);

  const loadExistingSessionIntoTimeline = useCallback(async (sessionId, sessionNotes) => {
    const exRes = await fetch(
      `${getApiUrl()}/api/training-session-exercises?training_session_id=${encodeURIComponent(String(sessionId))}`
    );
    if (!exRes.ok) {
      throw new Error('Failed to load session exercises');
    }
    const rows = await exRes.json();
    let list = Array.isArray(rows) ? rows : [];
    list = await hydrateExercisesFromSessionNotes(list, sessionNotes, sessionId);
    const flat = list.map(mapDbTrainingExerciseToSessionState);
    const types = buildSessionExerciseTypesFromRows(list);
    setSessionExercises(flat);
    setSessionExerciseTypes(types);
    setExpandedExerciseTypes(new Set(types.map((typeGroup) => typeGroup.exercise_type_id)));
    setExpandedSessionExerciseIds(new Set());
    return list.length;
  }, []);

  const focusTimelineExercise = useCallback((exerciseId, exerciseTypeId) => {
    if (exerciseTypeId != null) {
      setExpandedExerciseTypes(new Set([exerciseTypeId]));
    }
    if (exerciseId != null) {
      setExpandedSessionExerciseIds(new Set([exerciseId]));
    }
  }, []);

  const toggleSessionExerciseExpanded = useCallback((exerciseId) => {
    setExpandedSessionExerciseIds((prev) => {
      const next = new Set(prev);
      if (next.has(exerciseId)) next.delete(exerciseId);
      else next.add(exerciseId);
      return next;
    });
  }, []);

  const checkExistingSessionForDate = useCallback(async () => {
    if (!currentUser?.id) {
      setExistingSessionForDate(null);
      setExistingSessionsForDate([]);
      setAppendToExistingSession(false);
      return;
    }
    const dateKey = normalizeSessionDateInput(sessionDate, isUSUser);
    if (!isCompleteSessionDate(dateKey)) {
      setExistingSessionForDate(null);
      setExistingSessionsForDate([]);
      setAppendToExistingSession(false);
      return;
    }
    try {
      const sessions = sortSessionsForPicker(
        await findAllTrainingSessionsForStudentDate(currentUser.id, dateKey)
      );
      setExistingSessionsForDate(sessions);
      if (sessions.length === 0) {
        setExistingSessionForDate(null);
        setAppendToExistingSession(false);
        return;
      }
      if (sessions.length === 1) {
        setExistingSessionForDate(sessions[0]);
        return;
      }
      setExistingSessionForDate((prev) => {
        if (!prev?.id) return null;
        return sessions.find((row) => row.id === prev.id) ?? null;
      });
    } catch (error) {
      console.error('Error checking existing training session:', error);
      setExistingSessionForDate(null);
      setExistingSessionsForDate([]);
      setAppendToExistingSession(false);
    }
  }, [currentUser?.id, sessionDate, isUSUser]);

  useEffect(() => {
    void checkExistingSessionForDate();
  }, [checkExistingSessionForDate]);

  const clearTimelineForDateChange = useCallback(() => {
    setSessionExercises([]);
    setSessionExerciseTypes([]);
    setExpandedExerciseTypes(new Set());
    setExpandedSessionExerciseIds(new Set());
    setSelectedTimelineExerciseIds(new Set());
    setSelectedExerciseTypesForRoutine(new Set());
  }, []);

  const detachTimelineExercisesFromSavedSession = useCallback(() => {
    const stripSavedSessionLink = (exercise) => {
      if (!exercise.training_session_exercise_id) return exercise;
      return { ...exercise, training_session_exercise_id: null };
    };
    setSessionExercises((prev) => prev.map(stripSavedSessionLink));
    setSessionExerciseTypes((prev) =>
      prev.map((exerciseType) => ({
        ...exerciseType,
        exercises: exerciseType.exercises.map(stripSavedSessionLink),
      }))
    );
  }, []);

  const applySessionDateIso = useCallback(
    (isoOrDisplay, options = {}) => {
      const explicitClear = options.clearTimeline;
      const parsed = displayDateToIso(isoOrDisplay, isUSUser);
      const next = parsed || normalizeSessionDateInput(isoOrDisplay, isUSUser);
      const prevKey = normalizeSessionDateInput(sessionDate, isUSUser);
      const isFullDateChange =
        isCompleteSessionDate(prevKey) &&
        isCompleteSessionDate(next) &&
        next !== prevKey;

      if (next !== prevKey) {
        setExistingSessionForDate(null);
        setExistingSessionsForDate([]);
        setExistingSessionPickerModal(null);
        setAppendToExistingSession(false);
      }

      if (isFullDateChange && sessionExercises.length > 0 && explicitClear === undefined) {
        setDateChangeClearModal({ nextDate: next });
        return;
      }

      setSessionDate(next);

      if (isFullDateChange) {
        lastSavedSessionSignatureRef.current = null;
        if (explicitClear === false) {
          detachTimelineExercisesFromSavedSession();
        } else if (sessionExercises.length > 0) {
          clearTimelineForDateChange();
        }
      }
    },
    [
      sessionDate,
      isUSUser,
      sessionExercises.length,
      clearTimelineForDateChange,
      detachTimelineExercisesFromSavedSession,
    ]
  );

  const handleConfirmDateChangeClear = () => {
    const nextDate = dateChangeClearModal?.nextDate;
    setDateChangeClearModal(null);
    if (!nextDate) return;
    applySessionDateIso(nextDate, { clearTimeline: true });
  };

  const handleConfirmDateChangeKeep = () => {
    const nextDate = dateChangeClearModal?.nextDate;
    setDateChangeClearModal(null);
    if (!nextDate) return;
    applySessionDateIso(nextDate, { clearTimeline: false });
  };

  const handleSessionDateChange = (value) => {
    applySessionDateIso(value);
  };

  const handleSessionDateCalendarSelect = (iso) => {
    applySessionDateIso(iso);
    setShowSessionDateCalendar(false);
  };

  const applyAppendToExistingSession = async (session) => {
    if (!session?.id) return;
    setExistingSessionForDate(session);
    setAppendToExistingSession(true);
    setLoadingExistingSessionTimeline(true);
    try {
      if (session.notes) {
        setNotes(String(session.notes));
      }
      if (session.duration_minutes != null && session.duration_minutes !== '') {
        setDurationMinutes(String(session.duration_minutes));
      }
      await loadExistingSessionIntoTimeline(session.id, session.notes);
    } catch (error) {
      console.error('Error loading existing session:', error);
      setAppendToExistingSession(false);
      setExistingSessionForDate(null);
      setSessionExercises([]);
      setSessionExerciseTypes([]);
      setExpandedExerciseTypes(new Set());
      setExpandedSessionExerciseIds(new Set());
      Alert.alert('Error', 'Could not load the existing training session.');
    } finally {
      setLoadingExistingSessionTimeline(false);
    }
  };

  const closeExistingSessionPicker = useCallback(() => {
    if (pickerSelectingRef.current) return;
    setExistingSessionPickerModal(null);
    setPickerHighlightSessionId(null);
  }, []);

  const handleToggleAppendToExisting = async () => {
    if (appendToExistingSession) {
      setAppendToExistingSession(false);
      setLoadingExistingSessionTimeline(false);
      setSessionExercises([]);
      setSessionExerciseTypes([]);
      setExpandedExerciseTypes(new Set());
      setExpandedSessionExerciseIds(new Set());
      lastSavedSessionSignatureRef.current = null;
      return;
    }

    if (existingSessionPickerModal) {
      closeExistingSessionPicker();
      return;
    }

    if (!currentUser?.id) return;

    const dateKey = normalizeSessionDateInput(sessionDate, isUSUser);
    let sessions = existingSessionsForDate;
    if (
      !sessions.length ||
      !sessions.every((row) => normalizeDbSessionDate(row.session_date) === dateKey)
    ) {
      sessions = sortSessionsForPicker(
        await findAllTrainingSessionsForStudentDate(currentUser?.id, dateKey)
      );
      setExistingSessionsForDate(sessions);
    }

    if (sessions.length === 0) return;

    if (sessions.length > 1) {
      setPickerHighlightSessionId(null);
      setExistingSessionPickerModal({ sessions, dateKey });
      return;
    }

    await applyAppendToExistingSession(sessions[0]);
  };

  const handlePickExistingSession = async (session) => {
    if (!session?.id || pickerSelectingRef.current) return;
    pickerSelectingRef.current = true;
    setPickerHighlightSessionId(session.id);
    await new Promise((resolve) => setTimeout(resolve, 220));
    setExistingSessionPickerModal(null);
    try {
      await applyAppendToExistingSession(session);
    } catch (error) {
      console.error('Error selecting existing session:', error);
    } finally {
      setPickerHighlightSessionId(null);
      pickerSelectingRef.current = false;
    }
  };

  useEffect(() => {
    if (!appendToExistingSession || loadingExistingSessionTimeline) return;
    const timer = setTimeout(() => {
      sessionScrollRef.current?.scrollTo({ y: 0, animated: true });
    }, 150);
    return () => clearTimeout(timer);
  }, [
    appendToExistingSession,
    loadingExistingSessionTimeline,
    sessionExerciseTypes.length,
  ]);

  useEffect(() => {
    if (!showExerciseDetailModal) {
      setExerciseDetailGalleryRotationDeg(0);
      setExerciseImageLightbox(null);
    }
  }, [showExerciseDetailModal]);

  useEffect(() => {
    if (!selectedExerciseForDetail) return;
    setExerciseDetailGalleryRotationDeg(0);
  }, [selectedExerciseForDetail?.exercise_id, selectedExerciseForDetail?.id]);

  const fetchUser = async (userId) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/users/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(normalizeStudentUserRecord(data));
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/courses`);
      if (response.ok) {
        const data = await response.json();
        setCourses(dedupeCoursesForDropdown(Array.isArray(data) ? data : []));
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchGradesList = async () => {
    setLoadingGrades(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/grades`);
      if (response.ok) {
        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];
        const sorted = rows.sort(
          (a, b) =>
            (a.grade_rank_order ?? 0) - (b.grade_rank_order ?? 0) ||
            String(a.grade_name).localeCompare(String(b.grade_name))
        );
        setGrades(sorted);
        return sorted;
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
    } finally {
      setLoadingGrades(false);
    }
    return grades;
  };

  const loadExercisesForGrade = async (grade) => {
    if (!grade?.id || !currentUser?.id) {
      setExercises([]);
      return;
    }
    if (isGradeBeyondViewerAccess(grade, currentUser)) {
      setExercises([]);
      return;
    }
    setLoadingGradeExercises(true);
    try {
      const listUrl = exercisesListUrl({
        gradeId: grade.id,
        studentId: currentUser.id,
        viewerId: currentUser.id,
      });
      const response = await fetch(listUrl);
      if (response.ok) {
        const data = await response.json();
        setExercises(Array.isArray(data) ? data : []);
      } else {
        setExercises([]);
      }
    } catch (error) {
      console.error('Error fetching grade exercises:', error);
      setExercises([]);
    } finally {
      setLoadingGradeExercises(false);
    }
  };

  const handleGradeSelect = async (grade) => {
    if (isGradeBeyondViewerAccess(grade, currentUser)) return;
    setExercisePickerSource('grade');
    setSelectedGrade(grade);
    setShowGradesDropdown(false);
    setSelectedCourse(null);
    setSelectedExerciseType(null);
    setShowExerciseTypesDropdown(false);
    setShowExercisesDropdown(true);
    setSelectedExercises(new Set());
    setSelectAllExercises(false);
    await loadExercisesForGrade(grade);
  };

  const fetchRoutines = async (studentId) => {
    try {
      const owned = await fetchExerciseRoutines(studentId);
      setRoutines(Array.isArray(owned) ? owned : []);
      try {
        const shared = await fetchSharedExerciseRoutines(studentId);
        setSharedRoutines(Array.isArray(shared) ? shared : []);
      } catch (sharedError) {
        console.warn('Could not load shared routines:', sharedError);
        setSharedRoutines([]);
      }
    } catch (error) {
      console.error('Error fetching routines:', error);
      setRoutines([]);
      setSharedRoutines([]);
    }
  };

  const handleRoutineSelect = async (routine) => {
    setSelectedRoutine(routine);
    setShowRoutinesDropdown(false);

    const accessStudentId = currentUser?.id;
    const itemsUrl =
      accessStudentId != null
        ? `${getApiUrl()}/api/exercise-routines/${routine.id}/items?access_student_id=${encodeURIComponent(String(accessStudentId))}`
        : `${getApiUrl()}/api/exercise-routines/${routine.id}/items`;

    // Fetch routine items (exercises) for this routine
    try {
      const response = await fetch(itemsUrl);
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched routine items:', data);
        
        if (data.length === 0) {
          Alert.alert('No Exercises', 'This routine has no exercises available.');
          setSelectedRoutine(null);
          return;
        }
        
        // Group exercises by exercise type and maintain order
        const exercisesByType = {};
        data.forEach((item) => {
          const exerciseTypeId = item.exercise_type_id;
          if (!exercisesByType[exerciseTypeId]) {
            exercisesByType[exerciseTypeId] = {
              exercise_type_id: exerciseTypeId,
              exercise_type_name: item.exercise_type_name,
              exercises: [],
            };
          }
          exercisesByType[exerciseTypeId].exercises.push({
            id: item.exercise_id,
            exercise_id: item.exercise_id,
            exercise_name: item.exercise_name,
            sets: item.sets,
            reps: item.reps,
            exercise_type_id: item.exercise_type_id,
            order_index: item.order_index,
            display_url: item.display_url || null,
            display_images: item.display_images || null,
            description: item.description || null,
          });
        });
        
        // Sort exercises within each type by order_index
        Object.keys(exercisesByType).forEach((typeId) => {
          exercisesByType[typeId].exercises.sort((a, b) => a.order_index - b.order_index);
        });
        
        // Add exercises to session, grouped by exercise type
        const exerciseTypeArray = Object.values(exercisesByType);

        for (const exerciseType of exerciseTypeArray) {
          const exerciseData = await Promise.all(
            exerciseType.exercises.map((exercise) =>
              attachExerciseWithWeightDefaults(
                {
                  id: Date.now() + Math.random(),
                  exercise_id: exercise.exercise_id || exercise.id,
                  exercise_name: exercise.exercise_name,
                  exercise_type: exerciseType.exercise_type_name,
                  sets: exercise.sets || 1,
                  reps: exercise.reps || 1,
                  is_time_based_exercise: exercise.is_time_based_exercise,
                  is_height_based: exercise.is_height_based,
                  is_weight_based: exercise.is_weight_based,
                  time: exercise.time ?? null,
                  weight_kg: null,
                  height: null,
                  tempo: null,
                  exercise_type_id: exerciseType.exercise_type_id,
                  display_url: exercise.display_url || null,
                  display_images: exercise.display_images || null,
                  description: exercise.description || null,
                },
                currentUser?.id
              )
            )
          );

          setSessionExerciseTypes((prev) => {
            const existingExerciseTypeIndex = prev.findIndex(
              (et) => et.exercise_type_id === exerciseType.exercise_type_id
            );
            if (existingExerciseTypeIndex >= 0) {
              const updated = [...prev];
              updated[existingExerciseTypeIndex].exercises = [
                ...updated[existingExerciseTypeIndex].exercises,
                ...exerciseData,
              ];
              return updated;
            }
            return [
              ...prev,
              {
                id: Date.now(),
                exercise_type_id: exerciseType.exercise_type_id,
                exercise_type_name: exerciseType.exercise_type_name,
                exercises: exerciseData,
              },
            ];
          });

          setSessionExercises((prev) => [...prev, ...exerciseData]);
          const lastExercise = exerciseData[exerciseData.length - 1];
          if (lastExercise?.id != null) {
            focusTimelineExercise(lastExercise.id, exerciseType.exercise_type_id);
          }
        }

        // Reset routine selection
        setSelectedRoutine(null);
      } else {
        Alert.alert('Error', 'Failed to fetch routine exercises.');
        setSelectedRoutine(null);
      }
    } catch (error) {
      console.error('Error fetching routine items:', error);
      Alert.alert('Error', 'Failed to fetch routine exercises.');
      setSelectedRoutine(null);
    }
  };

  const handleCourseSelect = async (course) => {
    setExercisePickerSource('course');
    resetGradeExercisePicker();
    setShowGradeExercises(false);
    setSelectedCourse(course);
    setShowCoursesDropdown(false);
    setShowGoalsDropdown(false);
    
    // Fetch exercise types for this course
    try {
      const typesUrl = exerciseTypesByCourseUrl(course.id, {
        viewerId: currentUser?.id,
        studentId: currentUser?.id,
      });
      const response = await fetch(typesUrl);
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched exercise types:', data); // Debug log
        setExerciseTypes(data);
        if (data && data.length > 0) {
          setShowExerciseTypesDropdown(true);
        } else {
          Alert.alert('No Exercise Types', 'This course has no exercise types available.');
        }
      } else {
        console.error('Failed to fetch exercise types:', response.status);
      }
    } catch (error) {
      console.error('Error fetching exercise types:', error);
      Alert.alert('Error', 'Failed to load exercise types');
    }
  };

  const handleExerciseTypeSelect = async (exerciseType) => {
    setSelectedExerciseType(exerciseType);
    setShowExerciseTypesDropdown(false);
    setShowExercisesDropdown(true);
    setSelectedExercises(new Set()); // Reset selections
    setSelectAllExercises(false);
    
    // Fetch exercises for this exercise type
    try {
      const listUrl = exercisesListUrl({
        exerciseTypeId: exerciseType.id,
        courseId: selectedCourse?.id,
        studentId: currentUser?.id,
        viewerId: currentUser?.id,
      });
      const response = await fetch(listUrl);
      if (response.ok) {
        const data = await response.json();
        setExercises(data);
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
    }
  };

  const handleExerciseToggle = (exerciseId, event) => {
    // Stop event propagation to prevent opening detail modal
    if (event) {
      event.stopPropagation();
    }
    const newSelected = new Set(selectedExercises);
    if (newSelected.has(exerciseId)) {
      newSelected.delete(exerciseId);
    } else {
      newSelected.add(exerciseId);
    }
    setSelectedExercises(newSelected);
    setSelectAllExercises(newSelected.size === exercises.length && exercises.length > 0);
  };

  const handleExerciseDetailClick = (exercise) => {
    setExerciseCarouselVideoModal(null);
    setSelectedExerciseForDetail(exercise);
    setShowExerciseDetailModal(true);
  };

  const isYouTubeUrl = (url) => {
    if (!url) return false;
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    return youtubeRegex.test(url);
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    
    // Handle YouTube Shorts URLs: youtube.com/shorts/VIDEO_ID
    const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
    if (shortsMatch && shortsMatch[1]) {
      return `https://www.youtube.com/embed/${shortsMatch[1]}`;
    }
    
    // Handle standard YouTube URLs: youtu.be/VIDEO_ID
    const youtuBeMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (youtuBeMatch && youtuBeMatch[1]) {
      return `https://www.youtube.com/embed/${youtuBeMatch[1]}`;
    }
    
    // Extract video ID from various YouTube URL formats
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = match && match[2] && match[2].length === 11 ? match[2] : null;
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  const handleSelectAllToggle = () => {
    if (selectAllExercises) {
      setSelectedExercises(new Set());
      setSelectAllExercises(false);
    } else {
      const allIds = new Set(exercises.map(ex => ex.id));
      setSelectedExercises(allIds);
      setSelectAllExercises(true);
    }
  };

  const handleAddSelectedExercises = async () => {
    if (selectedExercises.size === 0) return;

    const selectedExercisesList = exercises.filter((ex) => selectedExercises.has(ex.id));

    if (exercisePickerSource === 'grade') {
      if (!selectedGrade) return;

      const grouped = new Map();
      selectedExercisesList.forEach((exercise) => {
        const typeId = exercise.exercise_type_id;
        if (typeId == null) return;
        if (!grouped.has(typeId)) {
          grouped.set(typeId, {
            exerciseTypeId: typeId,
            exerciseTypeName: exercise.exercise_type_name || 'Grade Exercise',
            exercises: [],
          });
        }
        grouped.get(typeId).exercises.push(exercise);
      });

      let updatedTypes = [...sessionExerciseTypes];
      let newFlat = [...sessionExercises];

      for (const group of grouped.values()) {
        const exerciseData = await Promise.all(
          group.exercises.map((exercise) =>
            attachExerciseWithWeightDefaults(
              {
                id: Date.now() + Math.random(),
                exercise_id: exercise.id,
                exercise_name: exercise.exercise_name,
                exercise_type: group.exerciseTypeName,
                sets: exercise.sets || 1,
                reps: exercise.reps || 1,
                is_time_based_exercise: exercise.is_time_based_exercise,
                is_height_based: exercise.is_height_based,
                is_weight_based: exercise.is_weight_based,
                time: exercise.time ?? null,
                weight_kg: exercise.weight_kg || null,
                height: exercise.height || null,
                tempo: exercise.tempo || null,
                grade_id: selectedGrade.id,
                grade_name: selectedGrade.grade_name,
                exercise_type_id: group.exerciseTypeId,
                display_url: exercise.display_url || null,
                display_images: exercise.display_images || null,
                description: exercise.description || null,
              },
              currentUser?.id
            )
          )
        );

        const existingIndex = updatedTypes.findIndex(
          (et) => et.exercise_type_id === group.exerciseTypeId
        );
        if (existingIndex >= 0) {
          updatedTypes[existingIndex].exercises = [
            ...updatedTypes[existingIndex].exercises,
            ...exerciseData,
          ];
        } else {
          updatedTypes.push({
            id: Date.now() + Math.random(),
            exercise_type_id: group.exerciseTypeId,
            exercise_type_name: group.exerciseTypeName,
            exercises: exerciseData,
            grade_id: selectedGrade.id,
            grade_name: selectedGrade.grade_name,
          });
        }
        newFlat = [...newFlat, ...exerciseData];
        setExpandedExerciseTypes((prev) => {
          const next = new Set(prev);
          next.add(group.exerciseTypeId);
          return next;
        });
      }

      setSessionExerciseTypes(updatedTypes);
      setSessionExercises(newFlat);

      if (selectedExercisesList.length > 0) {
        const last = selectedExercisesList[selectedExercisesList.length - 1];
        focusTimelineExercise(
          newFlat[newFlat.length - 1]?.id,
          last.exercise_type_id
        );
      }

      setShowExercisesDropdown(false);
      setShowGradesDropdown(false);
      setSelectedExercises(new Set());
      setSelectAllExercises(false);
      return;
    }

    if (!selectedExerciseType) return;
    
    const selectedExercisesListCourse = selectedExercisesList;
    
    // Check if this exerciseType already exists in sessionExerciseTypes
    const existingExerciseTypeIndex = sessionExerciseTypes.findIndex(
      et => et.exercise_type_id === selectedExerciseType.id
    );
    
    const exerciseData = await Promise.all(
      selectedExercisesListCourse.map((exercise) =>
        attachExerciseWithWeightDefaults(
          {
            id: Date.now() + Math.random(),
            exercise_id: exercise.id,
            exercise_name: exercise.exercise_name,
            exercise_type: selectedExerciseType.exercise_type_name,
            sets: exercise.sets || 1,
            reps: exercise.reps || 1,
            is_time_based_exercise: exercise.is_time_based_exercise,
            is_height_based: exercise.is_height_based,
            is_weight_based: exercise.is_weight_based,
            time: exercise.time ?? null,
            weight_kg: exercise.weight_kg || null,
            height: exercise.height || null,
            tempo: exercise.tempo || null,
            course_id: selectedCourse?.id,
            course_name: selectedCourse?.course_name,
            exercise_type_id: selectedExerciseType.id,
            display_url: exercise.display_url || null,
            display_images: exercise.display_images || null,
            description: exercise.description || null,
          },
          currentUser?.id
        )
      )
    );
    
    if (existingExerciseTypeIndex >= 0) {
      // Add exercises to existing exerciseType
      const updated = [...sessionExerciseTypes];
      updated[existingExerciseTypeIndex].exercises = [
        ...updated[existingExerciseTypeIndex].exercises,
        ...exerciseData
      ];
      setSessionExerciseTypes(updated);
    } else {
      // Create new exerciseType entry
      const newExerciseType = {
        id: Date.now(),
        exercise_type_id: selectedExerciseType.id,
        exercise_type_name: selectedExerciseType.exercise_type_name,
        exercises: exerciseData,
        course_id: selectedCourse?.id,
        course_name: selectedCourse?.course_name,
      };
      setSessionExerciseTypes([...sessionExerciseTypes, newExerciseType]);
    }
    
    // Also add to sessionExercises for saving to database
    setSessionExercises([...sessionExercises, ...exerciseData]);

    setExpandedExerciseTypes((prev) => {
      const next = new Set(prev);
      next.add(selectedExerciseType.id);
      return next;
    });
    
    setShowExercisesDropdown(false);
    setShowExerciseTypesDropdown(false);
    setSelectedCourse(null);
    setSelectedExerciseType(null);
    setExercisePickerSource(null);
    setExercises([]);
    setSelectedExercises(new Set());
    setSelectAllExercises(false);
  };

  const handleToggleExerciseType = (exerciseTypeId) => {
    const newExpanded = new Set(expandedExerciseTypes);
    if (newExpanded.has(exerciseTypeId)) {
      newExpanded.delete(exerciseTypeId);
    } else {
      newExpanded.add(exerciseTypeId);
    }
    setExpandedExerciseTypes(newExpanded);
  };

  const handleToggleExerciseTypeForRoutine = (exerciseTypeId, event) => {
    if (event) {
      event.stopPropagation();
    }
    const newSelected = new Set(selectedExerciseTypesForRoutine);
    if (newSelected.has(exerciseTypeId)) {
      newSelected.delete(exerciseTypeId);
    } else {
      newSelected.add(exerciseTypeId);
    }
    setSelectedExerciseTypesForRoutine(newSelected);
  };

  const handleToggleTimelineExerciseSelected = (exerciseId, event) => {
    if (event) {
      event.stopPropagation();
    }
    const key = String(exerciseId);
    setSelectedTimelineExerciseIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleRemoveTimelineExercisesByIds = async (rawIds) => {
    const idsToRemove = new Set(
      (Array.isArray(rawIds) ? rawIds : [rawIds])
        .filter((id) => id != null && id !== '')
        .map((id) => String(id))
    );
    if (idsToRemove.size === 0 || removingTimelineExercises) return;

    const exercisesToRemove = collectTimelineExercisesForRemoval(
      sessionExerciseTypes,
      idsToRemove
    );
    if (exercisesToRemove.length === 0) return;

    setRemovingTimelineExercises(true);
    try {
      for (const exercise of exercisesToRemove) {
        if (!exercise.training_session_exercise_id) continue;
        const delRes = await fetch(
          `${getApiUrl()}/api/training-session-exercises/${encodeURIComponent(String(exercise.training_session_exercise_id))}`,
          { method: 'DELETE' }
        );
        if (!delRes.ok) {
          throw new Error('Failed to remove exercise from saved session');
        }
      }

      setSessionExercises((prev) =>
        prev.filter((ex) => !idsToRemove.has(String(ex.id)))
      );
      const nextSessionExerciseTypes = sessionExerciseTypes
        .map((exerciseType) => ({
          ...exerciseType,
          exercises: exerciseType.exercises.filter(
            (ex) => !idsToRemove.has(String(ex.id))
          ),
        }))
        .filter((exerciseType) => exerciseType.exercises.length > 0);
      const remainingTypeIds = new Set(
        nextSessionExerciseTypes.map((et) => et.exercise_type_id)
      );
      setSessionExerciseTypes(nextSessionExerciseTypes);
      setSelectedExerciseTypesForRoutine(
        new Set([...selectedExerciseTypesForRoutine].filter((typeId) => remainingTypeIds.has(typeId)))
      );

      setSelectedTimelineExerciseIds((prev) => {
        const next = new Set(prev);
        for (const key of next) {
          if (idsToRemove.has(String(key))) next.delete(key);
        }
        return next;
      });
      setExpandedSessionExerciseIds((prev) => {
        const next = new Set(prev);
        idsToRemove.forEach((id) => next.delete(id));
        return next;
      });
    } catch (error) {
      console.error('Error removing timeline exercises:', error);
      Alert.alert('Error', error.message || 'Could not remove selected exercises.');
    } finally {
      setRemovingTimelineExercises(false);
    }
  };

  const handleRemoveSelectedTimelineExercises = async () => {
    await handleRemoveTimelineExercisesByIds([...selectedTimelineExerciseIds]);
  };

  const handleRemoveTimelineExercise = (exerciseId) => {
    void handleRemoveTimelineExercisesByIds([exerciseId]);
  };

  const handleSaveRoutine = async () => {
    if (!routineName.trim()) {
      Alert.alert('Error', 'Please enter a routine name');
      return;
    }

    if (!currentUser?.id) {
      Alert.alert('Error', 'User not found');
      return;
    }

    if (selectedExerciseTypesForRoutine.size === 0) {
      Alert.alert('Error', 'Please select at least one exercise type');
      return;
    }

    try {
      // Create ExerciseRoutine
      const routineResponse = await fetch(`${getApiUrl()}/api/exercise-routines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: currentUser.id,
          name: routineName.trim(),
          description: routineDescription.trim() || null,
        }),
      });

      if (!routineResponse.ok) {
        const errorData = await routineResponse.text();
        console.error('Failed to create routine:', routineResponse.status, errorData);
        throw new Error(`Failed to create routine: ${routineResponse.status} ${errorData}`);
      }

      const routine = await routineResponse.json();

      // Collect all exercises from selected exercise types in order
      const exercisesToSave = [];
      sessionExerciseTypes.forEach((exerciseType) => {
        if (selectedExerciseTypesForRoutine.has(exerciseType.exercise_type_id)) {
          exerciseType.exercises.forEach((exercise) => {
            exercisesToSave.push({
              exercise_routine_id: routine.id,
              exercise_id: exercise.exercise_id,
              order_index: exercisesToSave.length, // Sequential order index
            });
          });
        }
      });

      // Create ExerciseRoutineItem records
      for (const item of exercisesToSave) {
        const itemResponse = await fetch(`${getApiUrl()}/api/exercise-routine-items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item),
        });
        
        if (!itemResponse.ok) {
          const errorData = await itemResponse.text();
          console.error('Failed to create routine item:', itemResponse.status, errorData);
          throw new Error(`Failed to create routine item: ${itemResponse.status}`);
        }
      }

      Alert.alert('Success', 'Routine created successfully!');
      setShowRoutineModal(false);
      setRoutineName('');
      setRoutineDescription('');
      setSelectedExerciseTypesForRoutine(new Set());
      
      // Refresh routines list
      if (currentUser?.id) {
        fetchRoutines(currentUser.id);
      }
    } catch (error) {
      console.error('Error saving routine:', error);
      Alert.alert('Error', 'Failed to save routine. Please try again.');
    }
  };

  const flashTileRed = (animRef) => {
    Animated.sequence([
      Animated.timing(animRef, {
        toValue: 0, // Red color
        duration: 0,
        useNativeDriver: false,
      }),
      Animated.timing(animRef, {
        toValue: 1, // Original color
        duration: 1500,
        useNativeDriver: false,
      }),
    ]).start();
  };

  const resetGradeExercisePicker = () => {
    setSelectedGrade(null);
    setShowGradesDropdown(false);
    setLoadingGradeExercises(false);
  };

  const gradeExercisePickerActive =
    exercisePickerSource === 'grade' &&
    (showGradeExercises || selectedGrade || showExercisesDropdown);

  const handleCoursesTilePress = () => {
    // Check if sub-menu is active (exerciseType selected, exercises dropdown showing, or exerciseTypes dropdown showing)
    if (
      selectedExerciseType ||
      showExercisesDropdown ||
      showExerciseTypesDropdown ||
      gradeExercisePickerActive
    ) {
      flashTileRed(coursesTileColorAnim);
      return;
    }
    
    // If exercises or routines menu is open, close it and open courses
    if (showExercisesMenu || showRoutinesDropdown || showGoalsDropdown) {
      setShowExercisesMenu(false);
      setShowGradeExercises(false);
      resetGradeExercisePicker();
      setExercisePickerSource(null);
      setShowRoutinesDropdown(false);
      setShowGoalsDropdown(false);
    }
    
    setShowCoursesDropdown(!showCoursesDropdown);
    setShowExerciseTypesDropdown(false);
  };

  const handleRoutinesTilePress = () => {
    // Check if sub-menu is active (course selected, exerciseType selected, exercises dropdown showing, or exerciseTypes dropdown showing)
    if (
      selectedCourse ||
      selectedExerciseType ||
      showExercisesDropdown ||
      showExerciseTypesDropdown ||
      gradeExercisePickerActive
    ) {
      flashTileRed(routinesTileColorAnim);
      return;
    }
    
    // If courses or exercises menu is open, close it and open routines
    if (showCoursesDropdown || showExerciseTypesDropdown || showExercisesMenu || showGoalsDropdown) {
      setShowCoursesDropdown(false);
      setShowExerciseTypesDropdown(false);
      setShowExercisesMenu(false);
      setShowGradeExercises(false);
      resetGradeExercisePicker();
      setExercisePickerSource(null);
      setShowGoalsDropdown(false);
    }
    
    setShowRoutinesDropdown(!showRoutinesDropdown);
  };

  const handleExercisesTilePress = () => {
    // Check if sub-menu is active (course/routine/type picker — not grade exercises under this tile)
    if (
      selectedCourse ||
      selectedRoutine ||
      selectedExerciseType ||
      (showExercisesDropdown && exercisePickerSource !== 'grade') ||
      showExerciseTypesDropdown
    ) {
      flashTileRed(exercisesTileColorAnim);
      return;
    }
    
    // If courses or routines menu is open, close it and open exercises
    if (showCoursesDropdown || showRoutinesDropdown || showExerciseTypesDropdown || showGoalsDropdown) {
      setShowCoursesDropdown(false);
      setShowRoutinesDropdown(false);
      setShowExerciseTypesDropdown(false);
      setShowGoalsDropdown(false);
    }
    
    setShowExercisesMenu(!showExercisesMenu);
    if (showExercisesMenu) {
      setShowGradeExercises(false);
      resetGradeExercisePicker();
      setExercisePickerSource(null);
      setShowExercisesDropdown(false);
      setExercises([]);
    }
  };

  const fetchGoals = async () => {
    setLoadingGoals(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/goals`);
      if (response.ok) {
        const data = await response.json();
        setGoals(Array.isArray(data) ? data : []);
      } else {
        setGoals([]);
      }
    } catch (error) {
      console.error('Error fetching goals:', error);
      setGoals([]);
    } finally {
      setLoadingGoals(false);
    }
  };

  const fetchGoalMilestones = async (goalId) => {
    setLoadingGoalMilestonesId(goalId);
    try {
      const response = await fetch(`${getApiUrl()}/api/milestones?goal_id=${goalId}`);
      if (response.ok) {
        const data = await response.json();
        const milestones = (Array.isArray(data) ? data : []).sort((a, b) => {
          const aOrder = Number.isFinite(a.order_index) ? a.order_index : 0;
          const bOrder = Number.isFinite(b.order_index) ? b.order_index : 0;
          return aOrder - bOrder;
        });
        setGoalMilestonesByGoalId((prev) => ({ ...prev, [goalId]: milestones }));
        return milestones;
      }
    } catch (error) {
      console.error('Error fetching milestones for goal:', error);
    } finally {
      setLoadingGoalMilestonesId(null);
    }

    setGoalMilestonesByGoalId((prev) => ({ ...prev, [goalId]: [] }));
    return [];
  };

  const addExerciseToSessionState = async (newExercise) => {
    const withPerformance = await attachExerciseWithWeightDefaults(
      newExercise,
      currentUser?.id
    );
    setSessionExercises((prev) => [...prev, withPerformance]);

    setSessionExerciseTypes((prev) => {
      const existingExerciseTypeIndex = prev.findIndex(
        (et) => et.exercise_type_id === newExercise.exercise_type_id
      );

      if (existingExerciseTypeIndex >= 0) {
        const updated = [...prev];
        updated[existingExerciseTypeIndex].exercises = [
          ...updated[existingExerciseTypeIndex].exercises,
          withPerformance,
        ];
        return updated;
      }

      const newExerciseType = {
        id: Date.now() + Math.random(),
        exercise_type_id: withPerformance.exercise_type_id,
        exercise_type_name: withPerformance.exercise_type || 'Goal Exercise',
        exercises: [withPerformance],
      };
      return [...prev, newExerciseType];
    });

    focusTimelineExercise(withPerformance.id, withPerformance.exercise_type_id);
  };

  const handleGoalsTilePress = async () => {
    if (
      selectedCourse ||
      selectedRoutine ||
      selectedExerciseType ||
      (showExercisesDropdown && exercisePickerSource !== 'grade') ||
      showExerciseTypesDropdown ||
      gradeExercisePickerActive
    ) {
      flashTileRed(goalsTileColorAnim);
      return;
    }

    if (showCoursesDropdown || showRoutinesDropdown || showExercisesMenu) {
      setShowCoursesDropdown(false);
      setShowRoutinesDropdown(false);
      setShowExercisesMenu(false);
      setShowGradeExercises(false);
      resetGradeExercisePicker();
      setExercisePickerSource(null);
    }

    const next = !showGoalsDropdown;
    setShowGoalsDropdown(next);
    if (!next) {
      setExpandedGoalId(null);
      return;
    }

    await fetchGoals();
  };

  const handleGoalRowPress = async (goal) => {
    if (expandedGoalId === goal.id) {
      setExpandedGoalId(null);
      return;
    }

    setExpandedGoalId(goal.id);
    if (!goalMilestonesByGoalId[goal.id]) {
      await fetchGoalMilestones(goal.id);
    }
  };

  const handleSelectGoal = async (goal) => {
    try {
      if (!goal?.exercise_id) {
        Alert.alert('No Exercise', 'This goal has no associated exercise to add.');
        return;
      }

      const exerciseResponse = await fetch(
        exerciseByIdUrl(goal.exercise_id, {
          studentId: currentUser?.id,
          viewerId: currentUser?.id,
        })
      );
      if (!exerciseResponse.ok) {
        throw new Error('Failed to load goal exercise details');
      }
      const exercise = await exerciseResponse.json();

      const exerciseTypeName = exercise.exercise_type_name || 'Goal Exercise';
      const newExercise = {
        id: Date.now() + Math.random(),
        exercise_id: exercise.id,
        exercise_name: exercise.exercise_name || goal.exercise_name || goal.goal_name,
        exercise_type: exerciseTypeName,
        sets: exercise.sets || 1,
        reps: exercise.reps || 1,
        is_time_based_exercise: exercise.is_time_based_exercise,
        is_height_based: exercise.is_height_based,
        is_weight_based: exercise.is_weight_based,
        time: exercise.time ?? null,
        weight_kg: exercise.weight_kg || null,
        height: exercise.height || null,
        tempo: exercise.tempo || null,
        exercise_type_id: exercise.exercise_type_id || `goal-${goal.id}`,
        display_url: exercise.display_url || null,
        display_images: exercise.display_images || null,
        description: exercise.description || null,
        source_goal_id: goal.id,
      };

      await addExerciseToSessionState(newExercise);
      setShowGoalsDropdown(false);
      setExpandedGoalId(null);
    } catch (error) {
      console.error('Error selecting goal exercise:', error);
      Alert.alert('Error', 'Failed to add goal exercise to session.');
    }
  };

  const handleAddExercise = async (exercise) => {
    if (!selectedExerciseType) return;

    const newExercise = {
      id: Date.now() + Math.random(),
      exercise_id: exercise.id,
      exercise_name: exercise.exercise_name,
      exercise_type: selectedExerciseType.exercise_type_name,
      sets: exercise.sets || 1,
      reps: exercise.reps || 1,
      is_time_based_exercise: exercise.is_time_based_exercise,
      is_height_based: exercise.is_height_based,
      is_weight_based: exercise.is_weight_based,
      time: exercise.time ?? null,
      weight_kg: exercise.weight_kg || null,
      height: exercise.height || null,
      tempo: exercise.tempo || null,
      course_id: selectedCourse?.id,
      course_name: selectedCourse?.course_name,
      exercise_type_id: selectedExerciseType.id,
      display_url: exercise.display_url || null,
      display_images: exercise.display_images || null,
      description: exercise.description || null,
    };

    const withPerformance = await attachExerciseWithWeightDefaults(
      newExercise,
      currentUser?.id
    );
    
    // Check if this exerciseType already exists in sessionExerciseTypes
    const existingExerciseTypeIndex = sessionExerciseTypes.findIndex(
      et => et.exercise_type_id === selectedExerciseType.id
    );
    
    if (existingExerciseTypeIndex >= 0) {
      // Add exercise to existing exerciseType
      const updated = [...sessionExerciseTypes];
      updated[existingExerciseTypeIndex].exercises = [
        ...updated[existingExerciseTypeIndex].exercises,
        withPerformance
      ];
      setSessionExerciseTypes(updated);
    } else {
      // Create new exerciseType entry
      const newExerciseType = {
        id: Date.now(),
        exercise_type_id: selectedExerciseType.id,
        exercise_type_name: selectedExerciseType.exercise_type_name,
        exercises: [withPerformance],
        course_id: selectedCourse?.id,
        course_name: selectedCourse?.course_name,
      };
      setSessionExerciseTypes([...sessionExerciseTypes, newExerciseType]);
    }
    
    // Also add to sessionExercises for saving to database
    setSessionExercises([...sessionExercises, withPerformance]);

    focusTimelineExercise(withPerformance.id, selectedExerciseType.id);
    
    // Only reset if this was called from single exercise add (not batch add)
    if (!selectedExercises.has(exercise.id)) {
      setShowExercisesDropdown(false);
      setShowExerciseTypesDropdown(false);
      setSelectedCourse(null);
      setSelectedExerciseType(null);
      setExercisePickerSource(null);
      setExercises([]);
      setSelectedExercises(new Set());
      setSelectAllExercises(false);
    }
  };

  const handleCancelExerciseSelection = () => {
    setShowExercisesDropdown(false);
    setShowExerciseTypesDropdown(false);
    setShowGradesDropdown(false);
    setSelectedCourse(null);
    setSelectedExerciseType(null);
    if (exercisePickerSource === 'grade') {
      resetGradeExercisePicker();
    }
    setExercisePickerSource(null);
    setExercises([]);
    setSelectedExercises(new Set());
    setSelectAllExercises(false);
  };

  const handleShowGradeExercises = async () => {
    if (!currentUser?.id) return;
    const next = !showGradeExercises;
    setShowGradeExercises(next);

    if (!next) {
      if (exercisePickerSource === 'grade') {
        setShowExercisesDropdown(false);
        resetGradeExercisePicker();
        setExercisePickerSource(null);
        setExercises([]);
        setSelectedExercises(new Set());
        setSelectAllExercises(false);
      }
      return;
    }

    setExercisePickerSource('grade');
    setSelectedCourse(null);
    setSelectedRoutine(null);
    setSelectedExerciseType(null);
    setShowCoursesDropdown(false);
    setShowExerciseTypesDropdown(false);
    setShowGoalsDropdown(false);

    const list = grades.length > 0 ? grades : await fetchGradesList();
    const defaultGrade = findDefaultNextGrade(list, currentUser);
    if (defaultGrade && !isGradeBeyondViewerAccess(defaultGrade, currentUser)) {
      setSelectedGrade(defaultGrade);
      setShowExercisesDropdown(true);
      setSelectedExercises(new Set());
      setSelectAllExercises(false);
      await loadExercisesForGrade(defaultGrade);
    } else {
      setSelectedGrade(null);
      setExercises([]);
      setShowExercisesDropdown(false);
    }
  };

  const handleShowAssignedExercises = async () => {
    if (!currentUser?.id) return;

    setExercisePickerSource('assigned');
    setSelectedCourse(null);
    setSelectedRoutine(null);
    setSelectedExerciseType(null);
    setShowExercisesMenu(false);
    setShowGradeExercises(false);
    resetGradeExercisePicker();
    setShowExercisesDropdown(false);
    setExercises([]);
    setSelectedExercises(new Set());
    setSelectAllExercises(false);

    try {
      const response = await fetch(
        nonCourseExerciseTypesUrl({
          viewerId: currentUser.id,
          studentId: currentUser.id,
        })
      );
      if (response.ok) {
        const data = await response.json();
        setExerciseTypes(Array.isArray(data) ? data : []);
        if (data && data.length > 0) {
          setShowExerciseTypesDropdown(true);
        } else {
          setShowExerciseTypesDropdown(false);
          Alert.alert(
            'No Assigned Exercise Types',
            'You have no assigned exercises outside of courses or grades.'
          );
        }
      } else {
        Alert.alert('Error', 'Failed to load assigned exercise types.');
      }
    } catch (error) {
      console.error('Error fetching assigned exercise types:', error);
      Alert.alert('Error', 'Failed to load assigned exercise types.');
    }
  };

  const updateExerciseInSession = useCallback((exerciseId, updater) => {
    const apply = (ex) => (ex.id === exerciseId ? syncExercisePrescribedCounts(updater(ex)) : ex);
    setSessionExerciseTypes((prev) =>
      prev.map((et) => ({
        ...et,
        exercises: et.exercises.map(apply),
      }))
    );
    setSessionExercises((prev) => prev.map(apply));
  }, []);

  const syncSessionExercisesToDatabase = async (trainingSessionId) => {
    const existingRes = await fetch(
      `${getApiUrl()}/api/training-session-exercises?training_session_id=${encodeURIComponent(String(trainingSessionId))}`
    );
    if (!existingRes.ok) {
      throw new Error('Failed to load existing session exercises');
    }
    const existingRows = await existingRes.json();
    const existingDbIds = new Set(
      (Array.isArray(existingRows) ? existingRows : []).map((r) => r.id)
    );
    const keptDbIds = new Set();

    for (let i = 0; i < sessionExercises.length; i += 1) {
      const exercise = sessionExercises[i];
      const payload = buildSessionExercisePayload(exercise, i);

      if (exercise.training_session_exercise_id) {
        const putRes = await fetch(
          `${getApiUrl()}/api/training-session-exercises/${encodeURIComponent(String(exercise.training_session_exercise_id))}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }
        );
        if (!putRes.ok) {
          const message = await readSessionExerciseApiError(putRes, 'Failed to update session exercise');
          throw new Error(message);
        }
        keptDbIds.add(exercise.training_session_exercise_id);
      } else {
        const postRes = await fetch(`${getApiUrl()}/api/training-session-exercises`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            training_session_id: trainingSessionId,
            ...payload,
          }),
        });
        if (!postRes.ok) {
          const message = await readSessionExerciseApiError(postRes, 'Failed to create session exercise');
          throw new Error(message);
        }
        const created = await postRes.json();
        if (created?.id) keptDbIds.add(created.id);
      }
    }

    for (const dbId of existingDbIds) {
      if (!keptDbIds.has(dbId)) {
        const delRes = await fetch(
          `${getApiUrl()}/api/training-session-exercises/${encodeURIComponent(String(dbId))}`,
          { method: 'DELETE' }
        );
        if (!delRes.ok) {
          throw new Error('Failed to remove session exercise');
        }
      }
    }
  };

  const executeSaveSession = async () => {
    if (!currentUser?.id) {
      setSessionSaveToast({ type: 'error', message: SESSION_SAVE_ERROR_MESSAGE });
      return;
    }

    const normalizedDate = normalizeSessionDateInput(sessionDate, isUSUser);
    const updatingExisting =
      appendToExistingSession && existingSessionForDate?.id != null;

    const sessionSignature = buildSessionSaveSignature({
      currentUserId: currentUser.id,
      sessionDate: normalizedDate,
      durationMinutes,
      notes,
      sessionExercises,
    });

    if (saveInFlightRef.current) return;

    saveInFlightRef.current = true;
    setSavingSession(true);
    setSessionSaveToast(null);

    try {
      if (updatingExisting) {
        const sessionId = existingSessionForDate.id;
        const putRes = await fetch(
          `${getApiUrl()}/api/training-sessions/${encodeURIComponent(String(sessionId))}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              session_date: normalizedDate,
              duration_minutes: getSessionDurationMinutes(durationMinutes),
              notes: notes || null,
            }),
          }
        );
        if (!putRes.ok) {
          throw new Error('Failed to update session');
        }
        await syncSessionExercisesToDatabase(sessionId);
      } else {
        const sessionData = {
          student_id: currentUser.id,
          session_date: normalizedDate,
          duration_minutes: getSessionDurationMinutes(durationMinutes),
          notes: notes || null,
        };

        const sessionResponse = await fetch(`${getApiUrl()}/api/training-sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sessionData),
        });

        if (!sessionResponse.ok) {
          throw new Error('Failed to create session');
        }

        const session = await sessionResponse.json();
        await syncSessionExercisesToDatabase(session.id);
      }

      lastSavedSessionSignatureRef.current = sessionSignature;
      setSessionSaveToast({ type: 'success', message: SESSION_SAVE_SUCCESS_MESSAGE });
    } catch (error) {
      console.error('Error saving session:', error);
      setSessionSaveToast({ type: 'error', message: SESSION_SAVE_ERROR_MESSAGE });
    } finally {
      saveInFlightRef.current = false;
      setSavingSession(false);
    }
  };

  const handleSaveSession = async () => {
    if (!currentUser?.id) {
      setSessionSaveToast({ type: 'error', message: SESSION_SAVE_ERROR_MESSAGE });
      return;
    }

    if (sessionExercises.length === 0) {
      return;
    }

    if (saveInFlightRef.current) return;

    const normalizedDate = normalizeSessionDateInput(sessionDate, isUSUser);
    const updatingExisting =
      appendToExistingSession && existingSessionForDate?.id != null;

    if (!updatingExisting && sessionExercises.length > 0) {
      try {
        const duplicateSession = await findDuplicateTrainingSessionForExercises(
          currentUser.id,
          normalizedDate,
          sessionExercises
        );
        if (duplicateSession) {
          setDuplicateSessionModal({
            session: duplicateSession,
            dateKey: normalizedDate,
          });
          return;
        }
      } catch (error) {
        console.error('Error checking for duplicate training session:', error);
      }
    }

    await executeSaveSession();
  };

  const handleConfirmDuplicateSessionSave = async () => {
    setDuplicateSessionModal(null);
    await executeSaveSession();
  };

  const handleCancelSession = () => {
    Alert.alert(
      'Cancel Session',
      'Are you sure you want to cancel? All unsaved changes will be lost.',
      [
        { text: 'No', style: 'cancel' },
        { text: 'Yes', onPress: () => navigation.goBack() },
      ]
    );
  };

  const sessionBuilderTiles = [
    { key: 'courses', anim: coursesTileColorAnim, image: COURSES_IMAGE, label: 'Courses', onPress: handleCoursesTilePress },
    { key: 'routines', anim: routinesTileColorAnim, image: ROUTINES_IMAGE, label: 'Routines', onPress: handleRoutinesTilePress },
    { key: 'exercises', anim: exercisesTileColorAnim, image: EXERCISES_IMAGE, label: 'Exercises', onPress: handleExercisesTilePress },
    { key: 'goals', anim: goalsTileColorAnim, image: GOALS_IMAGE, label: 'Goals', onPress: handleGoalsTilePress },
  ];

  const estimatedSessionCarouselSlideW = Math.max(260, windowWidth - 124);

  const goSessionCarousel = (idx) => {
    const w = sessionTilesViewportW > 0 ? sessionTilesViewportW : estimatedSessionCarouselSlideW;
    if (w <= 0) return;
    const clamped = Math.max(0, Math.min(SESSION_BUILDER_TILE_COUNT - 1, idx));
    setSessionCarouselIndex(clamped);
    sessionTilesCarouselRef.current?.scrollTo({ x: clamped * w, animated: true });
  };

  useEffect(() => {
    sessionCarouselIndexRef.current = sessionCarouselIndex;
  }, [sessionCarouselIndex]);

  useEffect(() => {
    if (!isSessionTilesCarousel || sessionTilesViewportW <= 0) return;
    const idx = sessionCarouselIndexRef.current;
    sessionTilesCarouselRef.current?.scrollTo({ x: idx * sessionTilesViewportW, animated: false });
  }, [sessionTilesViewportW, isSessionTilesCarousel]);

  const sessionTilesCarouselModeRef = useRef(isSessionTilesCarousel);
  useEffect(() => {
    const wasCarousel = sessionTilesCarouselModeRef.current;
    sessionTilesCarouselModeRef.current = isSessionTilesCarousel;
    if (!isSessionTilesCarousel) return;
    if (wasCarousel) return;
    setSessionCarouselIndex(0);
    sessionCarouselIndexRef.current = 0;
    requestAnimationFrame(() => {
      sessionTilesCarouselRef.current?.scrollTo({ x: 0, animated: false });
    });
  }, [isSessionTilesCarousel]);

  const exerciseModalCarouselWidth = Math.max(160, Math.round(width * 0.9 - 48));
  const exerciseModalCarouselHeight = Math.round(
    exerciseModalCarouselWidth / EXERCISE_DETAIL_MODAL_CAROUSEL_ASPECT
  );
  const exerciseCarouselStyles = useMemo(() => {
    const base = createCourseMediaCarouselStyles(
      theme,
      exerciseModalCarouselWidth,
      exerciseModalCarouselHeight
    );
    const sheet = theme.isDarkMode ? theme.colors.surface : '#FFFFFF';
    return {
      ...base,
      carouselOuter: { ...base.carouselOuter, backgroundColor: sheet },
      carouselRow: { ...base.carouselRow, backgroundColor: sheet },
      carouselStage: { ...base.carouselStage, backgroundColor: sheet },
      carouselSlide: { ...base.carouselSlide, backgroundColor: sheet },
      carouselListWeb: { ...base.carouselListWeb, backgroundColor: sheet },
    };
  }, [theme, exerciseModalCarouselWidth, exerciseModalCarouselHeight]);
  const exerciseGalleryMediaUrls = useMemo(() => {
    if (!selectedExerciseForDetail) return [];
    return parseExerciseGalleryMediaUrls(selectedExerciseForDetail, exerciseGalleryIsYouTubeUrl);
  }, [selectedExerciseForDetail]);

  const showSessionExerciseRotateControls = useMemo(() => {
    if (!selectedExerciseForDetail) return false;
    if (exerciseGalleryMediaUrls.length > 0) return true;
    return !exerciseGalleryIsYouTubeUrl(selectedExerciseForDetail.display_url);
  }, [selectedExerciseForDetail, exerciseGalleryMediaUrls]);

  const exerciseImageLightboxImageMax = useMemo(() => {
    const horizontalPad = 18;
    const topReserve = 54;
    const bottomReserve = 72;
    const maxW = Math.max(80, windowWidth - horizontalPad * 2);
    const maxH = Math.max(80, windowHeight - topReserve - bottomReserve);
    return { w: maxW, h: maxH };
  }, [windowWidth, windowHeight]);

  const styles = getStyles(theme);

  const exerciseGalleryRotateIconColor = theme.colors.heading || '#d4af37';

  const exerciseGalleryRotateControls = (
    <View
      style={styles.modalExerciseGalleryRotateRow}
      {...(Platform.OS === 'web'
        ? { dataSet: { sessionExerciseModalRotate: 'true' } }
        : {})}
    >
      <TouchableOpacity
        style={styles.modalExerciseGalleryRotateHit}
        onPress={() =>
          setExerciseDetailGalleryRotationDeg((d) => (d - 90 + 360) % 360)
        }
        accessibilityRole="button"
        accessibilityLabel="Rotate image 90 degrees counter-clockwise"
      >
        <MaterialIcons name="rotate-left" size={26} color={exerciseGalleryRotateIconColor} />
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.modalExerciseGalleryRotateHit}
        onPress={() =>
          setExerciseDetailGalleryRotationDeg((d) => (d + 90) % 360)
        }
        accessibilityRole="button"
        accessibilityLabel="Rotate image 90 degrees clockwise"
      >
        <MaterialIcons name="rotate-right" size={26} color={exerciseGalleryRotateIconColor} />
      </TouchableOpacity>
    </View>
  );

  const exerciseImageLightboxRotateControls = (
    <View style={styles.exerciseImageLightboxRotateRow}>
      <TouchableOpacity
        style={styles.modalExerciseGalleryRotateHit}
        onPress={() =>
          setExerciseDetailGalleryRotationDeg((d) => (d - 90 + 360) % 360)
        }
        accessibilityRole="button"
        accessibilityLabel="Rotate image 90 degrees counter-clockwise"
      >
        <MaterialIcons name="rotate-left" size={26} color={exerciseGalleryRotateIconColor} />
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.modalExerciseGalleryRotateHit}
        onPress={() =>
          setExerciseDetailGalleryRotationDeg((d) => (d + 90) % 360)
        }
        accessibilityRole="button"
        accessibilityLabel="Rotate image 90 degrees clockwise"
      >
        <MaterialIcons name="rotate-right" size={26} color={exerciseGalleryRotateIconColor} />
      </TouchableOpacity>
    </View>
  );

  const exerciseGalleryRotateTransformStyle = {
    transform: [{ rotate: `${exerciseDetailGalleryRotationDeg}deg` }],
  };

  const renderSessionTimeline = () => (
    <>
      {sessionExerciseTypes.map((exerciseType, index) => {
        const isExpanded = expandedExerciseTypes.has(exerciseType.exercise_type_id);
        const isSelectedForRoutine = selectedExerciseTypesForRoutine.has(
          exerciseType.exercise_type_id
        );
        return (
          <View
            key={exerciseType.id || index}
            style={styles.timelineItem}
            {...(Platform.OS === 'web' ? { dataSet: { sessionTimelineItem: 'true' } } : {})}
          >
            <View
              style={styles.exerciseTypeRow}
              {...(Platform.OS === 'web' ? { dataSet: { sessionTimelineTypeRow: 'true' } } : {})}
            >
              <TouchableOpacity
                style={styles.routineCheckboxContainer}
                onPress={(e) =>
                  handleToggleExerciseTypeForRoutine(exerciseType.exercise_type_id, e)
                }
                activeOpacity={0.7}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: isSelectedForRoutine }}
                accessibilityLabel={`Include ${exerciseType.exercise_type_name} in routine`}
              >
                <View
                  style={[
                    styles.routineCheckbox,
                    isSelectedForRoutine && styles.routineCheckboxSelected,
                  ]}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { sessionTimelineCheckbox: 'true' } }
                    : {})}
                >
                  {isSelectedForRoutine && <Text style={styles.checkmark}>✓</Text>}
                </View>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.exerciseTypeContainer}
                onPress={() => handleToggleExerciseType(exerciseType.exercise_type_id)}
                activeOpacity={0.7}
                {...(Platform.OS === 'web'
                  ? { dataSet: { sessionTimelineTypeCard: 'true' } }
                  : {})}
              >
                <View style={styles.exerciseTypeIconContainer}>
                  <View style={styles.exerciseTypeIcon}>
                    <Text style={styles.exerciseTypeIconText}>
                      {exerciseType.exercise_type_name.substring(0, 2).toUpperCase()}
                    </Text>
                  </View>
                  <Text style={styles.exerciseTypeName}>{exerciseType.exercise_type_name}</Text>
                </View>
                <Text style={styles.expandArrow}>{isExpanded ? '▼' : '▶'}</Text>
              </TouchableOpacity>
            </View>

            {isExpanded && (
              <View
                style={styles.exercisesDropdown}
                {...(Platform.OS === 'web' ? { dataSet: { sessionTimelineDropdown: 'true' } } : {})}
              >
                {exerciseType.exercises.map((exercise, exIndex) => {
                  const isExerciseExpanded = expandedSessionExerciseIds.has(exercise.id);
                  const isSelectedForRemove = selectedTimelineExerciseIds.has(String(exercise.id));
                  return (
                  <View
                    key={exercise.id || exIndex}
                    style={styles.timelineExerciseItem}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { sessionTimelineExerciseItem: 'true' } }
                      : {})}
                  >
                    <View style={styles.timelineExerciseHeader}>
                      <TouchableOpacity
                        style={styles.routineCheckboxContainer}
                        onPress={(e) => handleToggleTimelineExerciseSelected(exercise.id, e)}
                        activeOpacity={0.7}
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked: isSelectedForRemove }}
                        accessibilityLabel={`Select ${exercise.exercise_name} for removal`}
                      >
                        <View
                          style={[
                            styles.routineCheckbox,
                            isSelectedForRemove && styles.routineCheckboxSelected,
                          ]}
                          {...(Platform.OS === 'web'
                            ? { dataSet: { sessionTimelineExerciseCheckbox: 'true' } }
                            : {})}
                        >
                          {isSelectedForRemove ? (
                            <Text style={styles.checkmark}>✓</Text>
                          ) : null}
                        </View>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.timelineExerciseHeaderMain}
                        onPress={() => handleExerciseDetailClick(exercise)}
                        activeOpacity={0.7}
                      >
                        <View style={styles.timelineExerciseIcon}>
                          <Text style={styles.timelineExerciseIconText}>
                            {exercise.exercise_name.substring(0, 2).toUpperCase()}
                          </Text>
                        </View>
                        <View style={styles.timelineExerciseInfo}>
                          <Text style={styles.timelineExerciseName}>{exercise.exercise_name}</Text>
                          <Text style={styles.timelineExerciseDetails}>
                            {formatExerciseSetsRepsSummary(exercise, weightInKg)}
                          </Text>
                        </View>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.timelineExerciseExpandBtn}
                        onPress={() => toggleSessionExerciseExpanded(exercise.id)}
                        activeOpacity={0.7}
                        accessibilityRole="button"
                        accessibilityLabel={
                          isExerciseExpanded
                            ? `Collapse sets and reps for ${exercise.exercise_name}`
                            : `Expand sets and reps for ${exercise.exercise_name}`
                        }
                      >
                        <Text style={styles.timelineExerciseExpandArrow}>
                          {isExerciseExpanded ? '▼' : '▶'}
                        </Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.timelineExerciseRemoveBtn}
                        onPress={() => handleRemoveTimelineExercise(exercise.id)}
                        disabled={removingTimelineExercises}
                        activeOpacity={0.7}
                        accessibilityRole="button"
                        accessibilityLabel={`Remove ${exercise.exercise_name} from session`}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { sessionTimelineExerciseRemove: 'true' } }
                          : {})}
                      >
                        <MaterialIcons
                          name="close"
                          size={22}
                          color={theme.isDarkMode ? '#ffb4ab' : '#c62828'}
                        />
                      </TouchableOpacity>
                    </View>
                    {isExerciseExpanded ? (
                      <SessionExerciseSetsEditor
                        exercise={exercise}
                        theme={theme}
                        studentId={currentUser?.id}
                        weightInKg={weightInKg}
                        excludeTrainingSessionId={
                          appendToExistingSession && existingSessionForDate?.id
                            ? existingSessionForDate.id
                            : null
                        }
                        onExerciseChange={(next) =>
                          updateExerciseInSession(exercise.id, () => next)
                        }
                      />
                    ) : null}
                  </View>
                  );
                })}
              </View>
            )}
          </View>
        );
      })}

      {(selectedExerciseTypesForRoutine.size > 0 || selectedTimelineExerciseIds.size > 0) ? (
        <View style={styles.timelineActionButtons}>
          {selectedExerciseTypesForRoutine.size > 0 ? (
            <TouchableOpacity
              style={styles.nameRoutineButton}
              onPress={() => setShowRoutineModal(true)}
              activeOpacity={0.7}
            >
              <Text style={styles.nameRoutineButtonText}>Name Routine</Text>
            </TouchableOpacity>
          ) : null}
          {selectedTimelineExerciseIds.size > 0 ? (
            <TouchableOpacity
              style={[
                styles.removeTimelineButton,
                removingTimelineExercises && styles.removeTimelineButtonDisabled,
              ]}
              onPress={() => void handleRemoveSelectedTimelineExercises()}
              disabled={removingTimelineExercises}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel="Remove selected exercises from session"
            >
              <Text style={styles.removeTimelineButtonText}>
                {removingTimelineExercises
                  ? 'Removing…'
                  : `Remove${selectedTimelineExerciseIds.size > 1 ? ` (${selectedTimelineExerciseIds.size})` : ''}`}
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}
    </>
  );

  const selectedSessionDateKey = normalizeSessionDateInput(sessionDate, isUSUser);
  const existingSessionMatchesSelectedDate = existingSessionsForDate.some(
    (session) => normalizeDbSessionDate(session.session_date) === selectedSessionDateKey
  );
  const appendCheckboxChecked =
    appendToExistingSession || !!existingSessionPickerModal;

  const renderExistingSessionPrompt = () => (
    <View
      style={styles.existingSessionPromptBlock}
      {...(Platform.OS === 'web' ? { dataSet: { sessionExistingPrompt: 'true' } } : {})}
    >
      <View style={styles.existingSessionCheckboxRow}>
        <TouchableOpacity
          style={styles.existingSessionCheckboxPressable}
          onPress={() => void handleToggleAppendToExisting()}
          activeOpacity={0.7}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: appendCheckboxChecked }}
        >
          <Text style={styles.existingSessionCheckboxMark}>
            {appendCheckboxChecked ? '☑' : '☐'}
          </Text>
          <Text style={styles.existingSessionCheckboxLabel}>
            {getExistingSessionPromptText(
              sessionDate,
              isUSUser,
              existingSessionsForDate.length > 1
            )}
          </Text>
        </TouchableOpacity>
        <Pressable
          style={styles.existingSessionInfoButton}
          onPress={() => setShowExistingSessionHelp((open) => !open)}
          accessibilityRole="button"
          accessibilityLabel="Information about adding to an existing session"
          accessibilityState={{ expanded: showExistingSessionHelp }}
          hitSlop={8}
          {...(Platform.OS === 'web'
            ? { dataSet: { sessionExistingHelpInfo: 'true' } }
            : {})}
        >
          <MaterialIcons
            name="info-outline"
            size={20}
            color={theme.colors.heading || theme.colors.primary || '#d4af37'}
          />
        </Pressable>
      </View>
      {showExistingSessionHelp ? (
        <View
          style={styles.existingSessionHelpBox}
          {...(Platform.OS === 'web'
            ? { dataSet: { sessionExistingHelpBox: 'true' } }
            : {})}
        >
          <Text style={styles.existingSessionHelpText}>{EXISTING_SESSION_HELP_TEXT}</Text>
        </View>
      ) : null}
      {appendToExistingSession && sessionExerciseTypes.length === 0 ? (
        <Text style={styles.existingSessionHint}>
          This saved session has no exercises yet. Add exercises in the timeline below, then save.
        </Text>
      ) : null}
    </View>
  );

  const hasTimelineExercises = sessionExercises.length > 0;
  const saveSessionDisabled = savingSession || !hasTimelineExercises;

  return (
    <View style={styles.container} data-screen="session-builder" data-theme={theme.isDarkMode ? 'dark' : 'light'}>
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      
      {/* Header */}
      <View style={styles.header} data-header="true">
        <View style={styles.hamburgerContainer} data-light-mode={!theme.isDarkMode} data-header-element="hamburger">
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer} data-light-mode={!theme.isDarkMode} data-header-element="breadcrumb">
          <Text style={styles.breadcrumb}>Session Builder</Text>
        </View>
        <View style={styles.profileIconContainer} data-light-mode={!theme.isDarkMode} data-header-element="profile">
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView
        ref={sessionScrollRef}
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
      >
        <TouchableOpacity
          style={styles.mainCalendarNav}
          onPress={() =>
            navigation.navigate('MyCalendar', {
              userId: currentUser?.id || route?.params?.userId,
              user: currentUser || route?.params?.user,
            })
          }
          accessibilityRole="button"
          accessibilityLabel="My Training Calendar"
          activeOpacity={0.75}
          {...(Platform.OS === 'web' ? { dataSet: { sessionCalendarNav: 'true' } } : {})}
        >
          <TrainingCalendarDayIcon size={56} />
          <Text style={styles.mainCalendarNavLabel}>My Training Calendar</Text>
        </TouchableOpacity>

        {/* Existing session — append to selected day's log */}
        {existingSessionMatchesSelectedDate ? renderExistingSessionPrompt() : null}

        {/* Session timeline — existing session or exercises added */}
        {(appendToExistingSession || sessionExerciseTypes.length > 0) && (
          <View
            style={styles.timelineContainer}
            {...(Platform.OS === 'web' ? { dataSet: { sessionTimeline: 'true' } } : {})}
          >
            {appendToExistingSession ? (
              <Text style={styles.timelineSectionTitle}>Training session exercises</Text>
            ) : sessionExerciseTypes.length > 0 ? (
              <Text style={styles.timelineSectionTitle}>Session exercises</Text>
            ) : null}
            {!appendToExistingSession && sessionExerciseTypes.length > 0 ? (
              <Text style={styles.timelineHintText}>
                Expand an exercise type to view exercises. Tap × to remove one, or select several
                and tap Remove.
              </Text>
            ) : null}
            {appendToExistingSession && loadingExistingSessionTimeline ? (
              <Text style={styles.timelineEmptyText}>Loading session exercises…</Text>
            ) : appendToExistingSession && sessionExerciseTypes.length === 0 ? (
              <Text style={styles.timelineEmptyText}>
                No exercises saved for this day yet. Use Courses or Exercises below to add
                them, then save.
              </Text>
            ) : (
              renderSessionTimeline()
            )}
          </View>
        )}

        {/* Courses and Exercises Tiles Container */}
        <View style={[styles.mainArea, isSessionTilesCarousel && styles.mainAreaCarousel]}>
          {!isSessionTilesCarousel ? (
            <View style={styles.tilesRow}>
              {sessionBuilderTiles.map((t) => (
                <Animated.View
                  key={t.key}
                  style={[
                    styles.coursesTile,
                    {
                      backgroundColor: t.anim.interpolate({
                        inputRange: [0, 1],
                        outputRange: ['#ff0000', theme.isDarkMode ? theme.colors.surface : '#FFFFFF'],
                      }),
                    },
                  ]}
                >
                  <View style={styles.coursesTileContentShell}>
                    <View style={styles.coursesTileImageWrap}>
                      <Image
                        source={t.image}
                        style={styles.coursesTileImage}
                        contentFit="contain"
                      />
                    </View>
                    <View style={styles.coursesTileButtonSlot} data-session-tile-button="true">
                      <TouchableOpacity
                        style={styles.coursesTileButton}
                        onPress={t.onPress}
                        activeOpacity={0.7}
                      >
                        <Text
                          style={styles.coursesTileButtonText}
                          numberOfLines={2}
                          ellipsizeMode="tail"
                        >
                          {t.label}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </Animated.View>
              ))}
            </View>
          ) : (
            <View style={styles.sessionTilesCarouselRow}>
              <TouchableOpacity
                style={[
                  styles.carouselArrowButton,
                  sessionCarouselIndex === 0 && styles.carouselArrowButtonDisabled,
                ]}
                onPress={() => goSessionCarousel(sessionCarouselIndex - 1)}
                disabled={sessionCarouselIndex === 0}
                accessibilityRole="button"
                accessibilityLabel="Previous session builder tile"
              >
                <Text style={styles.carouselArrowLabel}>‹</Text>
              </TouchableOpacity>

              <View
                style={styles.carouselViewport}
                onLayout={(e) => {
                  const w = Math.round(e.nativeEvent.layout.width);
                  if (w > 0 && w !== sessionTilesViewportW) {
                    setSessionTilesViewportW(w);
                  }
                }}
              >
                <ScrollView
                  ref={sessionTilesCarouselRef}
                  horizontal
                  pagingEnabled
                  style={styles.sessionTilesCarouselScroll}
                  contentContainerStyle={styles.sessionTilesCarouselScrollInner}
                  showsHorizontalScrollIndicator={false}
                  keyboardShouldPersistTaps="handled"
                  decelerationRate="fast"
                  {...Platform.select({
                    web: { nativeID: 'session-tiles-carousel-scroll' },
                    default: {},
                  })}
                  onMomentumScrollEnd={(e) => {
                    const w =
                      sessionTilesViewportW > 0 ? sessionTilesViewportW : estimatedSessionCarouselSlideW;
                    if (w <= 0) return;
                    const idx = Math.round(e.nativeEvent.contentOffset.x / w);
                    const clamped = Math.max(0, Math.min(SESSION_BUILDER_TILE_COUNT - 1, idx));
                    setSessionCarouselIndex(clamped);
                  }}
                >
                  {sessionBuilderTiles.map((t) => {
                    const slideW =
                      sessionTilesViewportW > 0 ? sessionTilesViewportW : estimatedSessionCarouselSlideW;
                    return (
                      <View key={t.key} style={[styles.carouselSlide, { width: slideW }]}>
                        <Animated.View
                          style={[
                            styles.coursesTile,
                            styles.coursesTileCarouselSlide,
                            {
                              backgroundColor: t.anim.interpolate({
                                inputRange: [0, 1],
                                outputRange: ['#ff0000', theme.isDarkMode ? theme.colors.surface : '#FFFFFF'],
                              }),
                            },
                          ]}
                        >
                          <View style={styles.coursesTileContentShell}>
                            <View style={[styles.coursesTileImageWrap, styles.coursesTileImageWrapCarousel]}>
                              <Image
                                source={t.image}
                                style={styles.coursesTileImageCarousel}
                                contentFit="contain"
                              />
                            </View>
                            <View style={styles.coursesTileButtonSlot} data-session-tile-button="true">
                              <TouchableOpacity
                                style={styles.coursesTileButton}
                                onPress={t.onPress}
                                activeOpacity={0.7}
                              >
                                <Text
                                  style={styles.coursesTileButtonText}
                                  numberOfLines={2}
                                  ellipsizeMode="tail"
                                >
                                  {t.label}
                                </Text>
                              </TouchableOpacity>
                            </View>
                          </View>
                        </Animated.View>
                      </View>
                    );
                  })}
                </ScrollView>
              </View>

              <TouchableOpacity
                style={[
                  styles.carouselArrowButton,
                  sessionCarouselIndex >= SESSION_BUILDER_TILE_COUNT - 1 && styles.carouselArrowButtonDisabled,
                ]}
                onPress={() => goSessionCarousel(sessionCarouselIndex + 1)}
                disabled={sessionCarouselIndex >= SESSION_BUILDER_TILE_COUNT - 1}
                accessibilityRole="button"
                accessibilityLabel="Next session builder tile"
              >
                <Text style={styles.carouselArrowLabel}>›</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Courses Dropdown */}
        {showCoursesDropdown && (
          <View
            style={styles.dropdown}
            {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
          >
            {enrolledCourses.length === 0 ? (
              <Text style={styles.dropdownItemText}>No enrolled courses.</Text>
            ) : (
              enrolledCourses.map((course) => (
              <TouchableOpacity
                key={course.id}
                style={styles.dropdownItem}
                onPress={() => handleCourseSelect(course)}
                {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
              >
                <Text style={styles.dropdownItemText}>{course.course_name}</Text>
              </TouchableOpacity>
              ))
            )}
            <TouchableOpacity
              style={styles.newButton}
              onPress={() => {
                setShowCoursesDropdown(false);
                // Show courses button again
              }}
            >
              <Text style={styles.newButtonText}>+ New Course</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Routines Dropdown */}
        {showRoutinesDropdown && (
          <View
            style={styles.dropdown}
            {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
          >
            {routines.length === 0 && sharedRoutines.length === 0 ? (
              <Text style={styles.dropdownItemText}>
                No routines found. Create a routine first.
              </Text>
            ) : (
              <>
                <Text style={styles.dropdownSectionTitle}>My Routines</Text>
                {routines.length === 0 ? (
                  <Text style={styles.dropdownItemSubtext}>No routines yet.</Text>
                ) : (
                  routines.map((routine) => (
                    <TouchableOpacity
                      key={`owned-${routine.id}`}
                      style={styles.dropdownItem}
                      onPress={() => handleRoutineSelect(routine)}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                    >
                      <Text style={styles.dropdownItemText}>{routine.name}</Text>
                      {routine.description ? (
                        <Text style={styles.dropdownItemSubtext}>{routine.description}</Text>
                      ) : null}
                    </TouchableOpacity>
                  ))
                )}
                {sharedRoutines.map((routine) => {
                  const ownerName = routine.owner_student_name || 'Student';
                  return (
                    <TouchableOpacity
                      key={`shared-${routine.id}`}
                      style={styles.sharedRoutineDropdownCard}
                      onPress={() => handleRoutineSelect(routine)}
                      {...(Platform.OS === 'web'
                        ? { dataSet: { sessionBuilderSharedRoutine: 'true' } }
                        : {})}
                    >
                      <View style={styles.sharedRoutineDropdownTitleRow}>
                        <StudentAvatar
                          studentId={routine.owner_student_id || routine.student_id}
                          studentName={ownerName}
                          profilePictureUrl={routine.owner_profile_picture_url}
                          webDataSet={{ sessionBuilderSharedRoutineAvatar: 'true' }}
                        />
                        <Text style={styles.sharedRoutineDropdownBadge}>Shared Routine</Text>
                      </View>
                      <View style={styles.sharedRoutineDropdownMetaRow}>
                        <Text style={styles.sharedRoutineDropdownName} numberOfLines={2}>
                          {routine.name}
                        </Text>
                        <Text style={styles.sharedRoutineDropdownOwner} numberOfLines={1}>
                          [{ownerName}]
                        </Text>
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </>
            )}
          </View>
        )}

        {/* Exercise Types Dropdown */}
        {showExerciseTypesDropdown && exerciseTypes.length > 0 && (
          <View
            style={styles.dropdown}
            {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
          >
            <Text style={styles.dropdownTitle}>
              {exercisePickerSource === 'assigned'
                ? 'Select Assigned Exercise Type:'
                : 'Select Exercise Type:'}
            </Text>
            {exerciseTypes.map((exerciseType) => (
              <TouchableOpacity
                key={exerciseType.id}
                style={styles.dropdownItem}
                onPress={() => handleExerciseTypeSelect(exerciseType)}
                {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
              >
                <Text style={styles.dropdownItemText}>{exerciseType.exercise_type_name}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
        {showExerciseTypesDropdown && exerciseTypes.length === 0 && (
          <View style={styles.dropdown}>
            <Text style={styles.dropdownItemText}>
              {exercisePickerSource === 'assigned'
                ? 'No assigned exercise types found.'
                : 'No exercise types found for this course.'}
            </Text>
          </View>
        )}

        {/* Exercises Dropdown */}
        {showExercisesDropdown && (exercisePickerSource !== 'grade' || selectedGrade) && (
          <View style={styles.dropdown}>
            {exercisePickerSource === 'grade' && selectedGrade ? (
              <Text style={styles.dropdownTitle}>
                {selectedGrade.grade_name} — select exercises
              </Text>
            ) : null}
            {loadingGradeExercises ? (
              <Text style={styles.dropdownItemText}>Loading exercises...</Text>
            ) : exercises.length === 0 ? (
              <Text style={styles.dropdownItemText}>
                {exercisePickerSource === 'grade'
                  ? 'No exercises available for this grade.'
                  : 'No exercises found.'}
              </Text>
            ) : null}
            {!loadingGradeExercises && exercises.length > 0 ? (
              <>
            {/* Select All Checkbox */}
            <View style={styles.selectAllContainer}>
              <TouchableOpacity
                style={styles.checkboxContainer}
                onPress={handleSelectAllToggle}
              >
                <View style={[
                  styles.checkbox,
                  selectAllExercises && styles.checkboxSelected
                ]}>
                  {selectAllExercises && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.selectAllText}>Select All</Text>
              </TouchableOpacity>
            </View>

            {exercises.map((exercise) => {
              const isSelected = selectedExercises.has(exercise.id);
              return (
                <TouchableOpacity
                  key={exercise.id}
                  style={[
                    styles.exerciseTile,
                    isSelected && styles.exerciseTileSelected
                  ]}
                  onPress={() => handleExerciseDetailClick(exercise)}
                  activeOpacity={0.7}
                >
                  <View
                    style={[
                      styles.exerciseTileInner,
                      isSelected && styles.exerciseTileInnerSelected,
                    ]}
                  >
                    {/* Yellow overlay for selected items */}
                    {isSelected && (
                      <View style={styles.yellowOverlay} />
                    )}
                    <View style={styles.exerciseContent}>
                    <View style={styles.exerciseIconWithName}>
                      <View style={styles.exerciseIconInDropdown}>
                        <Text style={styles.exerciseIconInDropdownText}>
                          {exercise.exercise_name.substring(0, 2).toUpperCase()}
                        </Text>
                      </View>
                      <View style={styles.exerciseInfo}>
                        <Text style={[
                          styles.exerciseName,
                          isSelected && styles.exerciseNameSelected
                        ]}>
                          {exercise.exercise_name}
                        </Text>
                        <Text style={[
                          styles.exerciseDetails,
                          isSelected && styles.exerciseDetailsSelected
                        ]}>
                          {exercise.sets} sets × {exercise.reps} reps
                        </Text>
                      </View>
                    </View>
                    <TouchableOpacity
                      style={styles.checkboxContainer}
                      onPress={(e) => handleExerciseToggle(exercise.id, e)}
                      activeOpacity={0.7}
                    >
                      <View style={[
                        styles.checkbox,
                        isSelected && styles.checkboxSelected
                      ]}>
                        {isSelected && <Text style={styles.checkmark}>✓</Text>}
                      </View>
                    </TouchableOpacity>
                  </View>
                  </View>
                </TouchableOpacity>
              );
            })}
            <View style={styles.actionButtons}>
              <TouchableOpacity
                style={[styles.actionButton, styles.cancelButton]}
                onPress={handleCancelExerciseSelection}
              >
                <Text style={styles.actionButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionButton, styles.addButton]}
                onPress={handleAddSelectedExercises}
                disabled={selectedExercises.size === 0}
              >
                <Text style={styles.actionButtonText}>
                  Add {selectedExercises.size > 0 ? `(${selectedExercises.size})` : ''}
                </Text>
              </TouchableOpacity>
            </View>
              </>
            ) : null}
          </View>
        )}

        {/* Exercises Menu */}
        {showExercisesMenu && (
          <View style={styles.dropdown}>
            {/* Grade Exercises */}
            <TouchableOpacity
              style={styles.menuHeader}
              onPress={handleShowGradeExercises}
            >
              <Text style={styles.menuHeaderText}>Grade Exercises</Text>
              <Text style={styles.menuArrow}>{showGradeExercises ? '▼' : '▶'}</Text>
            </TouchableOpacity>
            {showGradeExercises && (
              <View style={styles.subMenu}>
                <Text style={styles.gradePickerLabel}>Select Grade</Text>
                <TouchableOpacity
                  style={styles.gradePickerTrigger}
                  onPress={() => {
                    if (grades.length === 0) void fetchGradesList();
                    setShowGradesDropdown(!showGradesDropdown);
                  }}
                  activeOpacity={0.7}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { sessionGradePickerTrigger: 'true' } }
                    : {})}
                >
                  <Text style={styles.gradePickerTriggerText}>
                    {selectedGrade?.grade_name || 'Select Grade'}
                  </Text>
                  <Text style={styles.menuArrow}>{showGradesDropdown ? '▲' : '▼'}</Text>
                </TouchableOpacity>

                {showGradesDropdown && (
                  <View
                    style={styles.gradePickerDropdown}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { sessionGradePickerDropdown: 'true' } }
                      : {})}
                  >
                    {loadingGrades ? (
                      <Text style={styles.dropdownItemText}>Loading grades...</Text>
                    ) : grades.length === 0 ? (
                      <Text style={styles.dropdownItemText}>No grades found</Text>
                    ) : (
                      grades.map((grade) => {
                        const disabled = isGradeBeyondViewerAccess(grade, currentUser);
                        return (
                          <TouchableOpacity
                            key={grade.id}
                            style={[
                              styles.dropdownItem,
                              disabled && styles.dropdownItemDisabled,
                            ]}
                            onPress={() => {
                              if (!disabled) void handleGradeSelect(grade);
                            }}
                            disabled={disabled}
                            activeOpacity={disabled ? 1 : 0.7}
                            {...(Platform.OS === 'web'
                              ? { dataSet: { dropdownRow: 'true' } }
                              : {})}
                          >
                            <Text
                              style={[
                                styles.dropdownItemText,
                                disabled && styles.dropdownItemTextDisabled,
                              ]}
                            >
                              {grade.grade_name}
                            </Text>
                          </TouchableOpacity>
                        );
                      })
                    )}
                  </View>
                )}

                {selectedGrade && isGradeBeyondViewerAccess(selectedGrade, currentUser) ? (
                  <Text style={styles.gradePickerHint}>
                    Exercises for this grade are not available at your rank (you may view up to one
                    grade above your own).
                  </Text>
                ) : null}
              </View>
            )}

            {/* Assigned Exercises — non-course / non-grade types with assignment access */}
            <TouchableOpacity
              style={styles.menuHeader}
              onPress={handleShowAssignedExercises}
            >
              <Text style={styles.menuHeaderText}>Assigned Exercises</Text>
              <Text style={styles.menuArrow}>▶</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Goals Dropdown */}
        {showGoalsDropdown && (
          <View style={styles.dropdown}>
            <Text style={styles.dropdownTitle}>Select Goal</Text>
            {loadingGoals ? (
              <Text style={styles.dropdownItemText}>Loading goals...</Text>
            ) : goals.length === 0 ? (
              <Text style={styles.dropdownItemText}>No goals found</Text>
            ) : (
              goals.map((goal) => {
                const isExpanded = expandedGoalId === goal.id;
                const milestones = goalMilestonesByGoalId[goal.id] || [];
                const isLoadingMilestones = loadingGoalMilestonesId === goal.id;

                return (
                  <View key={goal.id} style={styles.goalRowWrapper}>
                    <TouchableOpacity
                      style={[styles.goalRow, isExpanded && styles.goalRowExpanded]}
                      onPress={() => handleGoalRowPress(goal)}
                      activeOpacity={0.8}
                    >
                      <View style={styles.goalRowMain}>
                        <Text style={styles.goalRowTitle}>{goal.goal_name || 'Untitled Goal'}</Text>
                        <Text style={styles.goalRowSubtext}>
                          Exercise: {goal.exercise_name || 'No exercise assigned'}
                        </Text>
                      </View>
                      <TouchableOpacity
                        style={styles.goalSelectButton}
                        onPress={(event) => {
                          event?.stopPropagation?.();
                          handleSelectGoal(goal);
                        }}
                      >
                        <Text style={styles.goalSelectButtonText}>Select</Text>
                      </TouchableOpacity>
                    </TouchableOpacity>

                    {isExpanded && (
                      <View style={styles.goalMilestonesPanel}>
                        {isLoadingMilestones ? (
                          <Text style={styles.goalMilestoneText}>Loading milestones...</Text>
                        ) : milestones.length === 0 ? (
                          <Text style={styles.goalMilestoneText}>No associated milestones</Text>
                        ) : (
                          milestones.map((milestone, index) => (
                            <Text key={`${goal.id}-milestone-${milestone.id || index}`} style={styles.goalMilestoneText}>
                              Milestone {index + 1}: {milestone.name || 'Untitled'}
                            </Text>
                          ))
                        )}
                      </View>
                    )}
                  </View>
                );
              })
            )}
          </View>
        )}

        {/* Advanced Section */}
        <View
          style={styles.advancedSection}
          {...(Platform.OS === 'web' ? { dataSet: { sessionAdvanced: 'true' } } : {})}
        >
          <TouchableOpacity
            style={styles.advancedHeader}
            onPress={() => setShowAdvanced(!showAdvanced)}
          >
            <Text style={styles.advancedArrow}>{showAdvanced ? '▼' : '▶'}</Text>
            <Text style={styles.advancedTitle}>Advanced</Text>
          </TouchableOpacity>

          {showAdvanced && (
            <View style={styles.advancedForm}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={styles.textArea}
              multiline
              numberOfLines={4}
              value={notes}
              onChangeText={setNotes}
              placeholder="Add notes..."
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
            />

            <Text style={styles.label}>What day did you train this session on?</Text>
            <View
              style={styles.sessionDateInputWrap}
              {...(Platform.OS === 'web' ? { dataSet: { sessionDateField: 'true' } } : {})}
            >
              <TextInput
                style={[styles.input, styles.sessionDateInput]}
                value={isoDateToDisplay(
                  normalizeSessionDateInput(sessionDate, isUSUser),
                  isUSUser
                )}
                onChangeText={handleSessionDateChange}
                placeholder={dateInputPlaceholder(isUSUser)}
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
              />
              <TouchableOpacity
                style={styles.sessionDateCalendarIconButton}
                onPress={() => setShowSessionDateCalendar(true)}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel="Open calendar to pick training date"
                nativeID="session-date-calendar-icon"
                {...(Platform.OS === 'web'
                  ? { dataSet: { sessionDateCalendarIcon: 'true' } }
                  : {})}
              >
                <MaterialIcons
                  name="calendar-today"
                  size={22}
                  color={theme.colors.heading || '#d4af37'}
                />
              </TouchableOpacity>
            </View>
            <SessionDateCalendarModal
              visible={showSessionDateCalendar}
              onClose={() => setShowSessionDateCalendar(false)}
              selectedIso={normalizeSessionDateInput(sessionDate, isUSUser)}
              onSelectDate={handleSessionDateCalendarSelect}
              isUSUser={isUSUser}
              studentId={currentUser?.id}
              assignedCourses={parseAssignedCourses(currentUser?.courses)}
            />

            {existingSessionMatchesSelectedDate ? renderExistingSessionPrompt() : null}

            <Text style={styles.label}>Duration of session (in minutes)</Text>
            <TextInput
              style={styles.input}
              value={durationMinutes}
              onChangeText={setDurationMinutes}
              keyboardType="numeric"
              placeholder="Enter duration"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
            />
            </View>
          )}
        </View>

        {/* Action Buttons */}
        <View style={styles.bottomButtons}>
          <TouchableOpacity
            style={[styles.bottomButton, styles.cancelButton]}
            onPress={handleCancelSession}
          >
            <Text style={styles.bottomButtonText}>Cancel Session</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.bottomButton,
              styles.saveButton,
              !hasTimelineExercises && styles.saveButtonDisabled,
              savingSession && styles.bottomButtonDisabled,
            ]}
            onPress={handleSaveSession}
            disabled={saveSessionDisabled}
            accessibilityState={{ disabled: saveSessionDisabled }}
          >
            {savingSession ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text
                style={[
                  styles.bottomButtonText,
                  !hasTimelineExercises && styles.saveButtonTextDisabled,
                ]}
              >
                Save Session
              </Text>
            )}
          </TouchableOpacity>
        </View>

        {sessionSaveToast ? (
          <View
            style={[
              styles.sessionSaveToast,
              sessionSaveToast.type === 'error'
                ? styles.sessionSaveToastError
                : styles.sessionSaveToastSuccess,
            ]}
            nativeID="session-builder-save-toast"
            {...(Platform.OS === 'web'
              ? {
                  dataSet: {
                    sessionSaveToast: 'true',
                    sessionSaveToastType: sessionSaveToast.type,
                  },
                }
              : {})}
          >
            <Text
              style={styles.sessionSaveToastMessage}
              {...(Platform.OS === 'web'
                ? { dataSet: { sessionSaveToastText: 'true' } }
                : {})}
            >
              {sessionSaveToast.message}
            </Text>
            <TouchableOpacity
              onPress={() => setSessionSaveToast(null)}
              style={styles.sessionSaveToastClose}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Dismiss"
              nativeID="session-builder-save-toast-close"
              {...(Platform.OS === 'web'
                ? { dataSet: { sessionSaveToastClose: 'true' } }
                : {})}
            >
              <Text style={styles.sessionSaveToastCloseText}>✕</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </ScrollView>

      {/* Exercise Detail Modal */}
      <Modal
        visible={showExerciseDetailModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {
          setShowExerciseDetailModal(false);
          setExerciseCarouselVideoModal(null);
          setExerciseImageLightbox(null);
        }}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => {
            setShowExerciseDetailModal(false);
            setExerciseCarouselVideoModal(null);
            setExerciseImageLightbox(null);
          }}
        >
          <View
            style={styles.modalContent}
            onStartShouldSetResponder={() => true}
            {...(Platform.OS === 'web'
              ? { dataSet: { sessionExerciseDetailModal: 'true' } }
              : {})}
          >
            {selectedExerciseForDetail && (
              <>
                {/* Close Button */}
                <TouchableOpacity
                  style={styles.modalCloseButton}
                  onPress={() => {
                    setShowExerciseDetailModal(false);
                    setExerciseCarouselVideoModal(null);
                    setExerciseImageLightbox(null);
                  }}
                  activeOpacity={0.7}
                >
                  <Text style={styles.modalCloseButtonText}>✕</Text>
                </TouchableOpacity>

                {/* Exercise Name */}
                <Text style={styles.modalExerciseName}>
                  {selectedExerciseForDetail.exercise_name}
                </Text>

                {/* Video (display_url) + image carousel (display_images / non-YouTube display_url) */}
                <View
                  style={styles.modalMediaContainer}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { sessionExerciseModalMedia: 'true' } }
                    : {})}
                >
                  {isYouTubeUrl(selectedExerciseForDetail.display_url) ? (
                    Platform.OS === 'web' ? (
                      <View style={styles.youtubeContainer}>
                        <iframe
                          src={getYouTubeEmbedUrl(selectedExerciseForDetail.display_url)}
                          style={styles.youtubeIframe}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </View>
                    ) : (
                      <View style={styles.youtubeContainer}>
                        <Text style={styles.youtubeLinkText}>
                          YouTube Video:{'\n'}
                          {selectedExerciseForDetail.display_url}
                        </Text>
                        <Text style={styles.youtubeNoteText}>
                          (Video playback available on web version)
                        </Text>
                      </View>
                    )
                  ) : null}

                  {exerciseGalleryMediaUrls.length > 0 ? (
                    <View
                      style={
                        isYouTubeUrl(selectedExerciseForDetail.display_url)
                          ? styles.modalExerciseCarouselBelowVideo
                          : null
                      }
                    >
                      <View
                        style={[
                          styles.modalExerciseCarouselTransformWrap,
                          exerciseGalleryRotateTransformStyle,
                        ]}
                      >
                        <CourseMediaCarousel
                          mediaUrls={exerciseGalleryMediaUrls}
                          slideWidth={exerciseModalCarouselWidth}
                          slideHeight={exerciseModalCarouselHeight}
                          theme={theme}
                          styles={exerciseCarouselStyles}
                          onOpenVideo={(uri, title) =>
                            setExerciseCarouselVideoModal({
                              uri,
                              title: title || 'Video',
                            })
                          }
                          onPressImage={(url) =>
                            setExerciseImageLightbox({ kind: 'remote', uri: url })
                          }
                          pauseAutoRotate={!!exerciseCarouselVideoModal || !!exerciseImageLightbox}
                        />
                      </View>
                    </View>
                  ) : null}

                  {exerciseGalleryMediaUrls.length === 0 &&
                  !isYouTubeUrl(selectedExerciseForDetail.display_url) ? (
                    <Pressable
                      style={[styles.modalImageRotateWrap, exerciseGalleryRotateTransformStyle]}
                      onPress={() => {
                        if (selectedExerciseForDetail.display_url) {
                          setExerciseImageLightbox({
                            kind: 'remote',
                            uri: selectedExerciseForDetail.display_url,
                          });
                        } else {
                          setExerciseImageLightbox({
                            kind: 'asset',
                            source: PLACEHOLDER_IMAGE,
                          });
                        }
                      }}
                      accessibilityRole="button"
                      accessibilityLabel="View larger image"
                    >
                      <Image
                        source={
                          selectedExerciseForDetail.display_url
                            ? { uri: selectedExerciseForDetail.display_url }
                            : PLACEHOLDER_IMAGE
                        }
                        style={styles.modalImage}
                        contentFit="contain"
                        onError={() => {
                          console.log('Image failed to load, using placeholder');
                        }}
                      />
                    </Pressable>
                  ) : null}
                </View>

                <View
                  style={styles.modalExerciseDetailLower}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { sessionExerciseDetailModalBody: 'true' } }
                    : {})}
                >
                  {showSessionExerciseRotateControls ? exerciseGalleryRotateControls : null}

                  {/* Description */}
                  {selectedExerciseForDetail.description ? (
                    <View
                      style={styles.modalDescriptionContainer}
                      {...(Platform.OS === 'web'
                        ? { dataSet: { sessionExerciseModalDescription: 'true' } }
                        : {})}
                    >
                      <Text style={styles.modalDescriptionLabel}>Description:</Text>
                      <Text style={styles.modalDescriptionText}>
                        {selectedExerciseForDetail.description}
                      </Text>
                    </View>
                  ) : null}

                  {/* Exercise Details */}
                  <View
                    style={styles.modalExerciseDetails}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { sessionExerciseModalSetsReps: 'true' } }
                      : {})}
                  >
                    <Text style={styles.modalExerciseDetailsText}>
                      {selectedExerciseForDetail.sets} sets × {selectedExerciseForDetail.reps} reps
                    </Text>
                  </View>
                </View>
              </>
            )}
          </View>
        </TouchableOpacity>
      </Modal>

      <Modal
        visible={!!exerciseCarouselVideoModal}
        transparent
        animationType="fade"
        supportedOrientations={['portrait', 'landscape']}
        onRequestClose={() => setExerciseCarouselVideoModal(null)}
      >
        <View style={styles.exerciseCarouselVideoModalRoot}>
          <TouchableOpacity
            style={StyleSheet.absoluteFillObject}
            activeOpacity={1}
            onPress={() => setExerciseCarouselVideoModal(null)}
          />
          <View style={styles.exerciseCarouselVideoModalSheet} pointerEvents="box-none">
            <TouchableOpacity
              style={styles.exerciseCarouselVideoModalClose}
              onPress={() => setExerciseCarouselVideoModal(null)}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Text style={styles.exerciseCarouselVideoModalCloseText}>✕</Text>
            </TouchableOpacity>
            {Platform.OS === 'web' && exerciseCarouselVideoModal?.uri ? (
              <View style={styles.exerciseCarouselVideoFrame}>
                {React.createElement('iframe', {
                  title: exerciseCarouselVideoModal.title || 'Video',
                  src: exerciseCarouselVideoModal.uri,
                  style: {
                    flex: 1,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: '#000',
                  },
                  allow:
                    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
                  allowFullScreen: true,
                })}
              </View>
            ) : exerciseCarouselVideoModal?.uri ? (
                  <WebView
                    source={{ uri: exerciseCarouselVideoModal.uri }}
                    style={styles.exerciseCarouselVideoWebView}
                    allowsFullscreenVideo
                    allowsInlineMediaPlayback
                    javaScriptEnabled
                    domStorageEnabled
                    mediaPlaybackRequiresUserAction={false}
                  />
                ) : null}
          </View>
        </View>
      </Modal>

      <Modal
        visible={!!exerciseImageLightbox}
        transparent
        animationType="fade"
        supportedOrientations={['portrait', 'landscape']}
        onRequestClose={() => setExerciseImageLightbox(null)}
      >
        <View style={styles.exerciseImageLightboxRoot}>
          <TouchableOpacity
            style={StyleSheet.absoluteFillObject}
            activeOpacity={1}
            onPress={() => setExerciseImageLightbox(null)}
          />
          <View style={styles.exerciseImageLightboxSheet} pointerEvents="box-none">
            <TouchableOpacity
              style={styles.exerciseImageLightboxClose}
              onPress={() => setExerciseImageLightbox(null)}
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              accessibilityRole="button"
              accessibilityLabel="Close enlarged image"
            >
              <Text style={styles.exerciseImageLightboxCloseText}>✕</Text>
            </TouchableOpacity>
            <View style={[styles.exerciseImageLightboxStage, exerciseGalleryRotateTransformStyle]}>
              {exerciseImageLightbox?.kind === 'remote' ? (
                <Image
                  source={{ uri: resolveRemoteUri(exerciseImageLightbox.uri) }}
                  style={[
                    styles.exerciseImageLightboxImage,
                    {
                      width: exerciseImageLightboxImageMax.w,
                      height: exerciseImageLightboxImageMax.h,
                    },
                  ]}
                  contentFit="contain"
                />
              ) : exerciseImageLightbox?.kind === 'asset' ? (
                <Image
                  source={exerciseImageLightbox.source}
                  style={[
                    styles.exerciseImageLightboxImage,
                    {
                      width: exerciseImageLightboxImageMax.w,
                      height: exerciseImageLightboxImageMax.h,
                    },
                  ]}
                  contentFit="contain"
                />
              ) : null}
            </View>
            {exerciseImageLightboxRotateControls}
          </View>
        </View>
      </Modal>

      <Modal
        visible={!!existingSessionPickerModal}
        transparent
        animationType="fade"
        onRequestClose={closeExistingSessionPicker}
      >
        <Pressable style={styles.modalOverlay} onPress={closeExistingSessionPicker}>
          <Pressable
            style={[styles.modalContent, styles.existingSessionPickerModalContent]}
            onPress={(event) => event.stopPropagation?.()}
            {...(Platform.OS === 'web' ? { dataSet: { sessionExistingPickerModal: 'true' } } : {})}
          >
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={closeExistingSessionPicker}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Close session picker"
            >
              <Text style={styles.modalCloseButtonText}>✕</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Choose a training session</Text>
            <Text style={styles.existingSessionPickerSubtitle}>
              Tap a session below to add your exercises to it for{' '}
              {formatExistingSessionDateLabel(
                existingSessionPickerModal?.dateKey || sessionDate,
                isUSUser
              )}
              .
            </Text>
            <ScrollView
              style={styles.existingSessionPickerScroll}
              contentContainerStyle={styles.existingSessionPickerScrollContent}
              showsVerticalScrollIndicator
              nestedScrollEnabled
            >
              {(existingSessionPickerModal?.sessions ?? []).map((session, index) => {
                const isSelected = pickerHighlightSessionId === session.id;
                return (
                  <TouchableOpacity
                    key={session.id ?? index}
                    onPress={() => void handlePickExistingSession(session)}
                    activeOpacity={0.88}
                    disabled={pickerHighlightSessionId != null && !isSelected}
                    style={[
                      styles.existingSessionPickerRow,
                      isSelected && styles.existingSessionPickerRowSelected,
                    ]}
                    {...(Platform.OS === 'web'
                      ? {
                          dataSet: {
                            sessionExistingPickerRow: 'true',
                            ...(isSelected ? { sessionExistingPickerSelected: 'true' } : {}),
                          },
                        }
                      : {})}
                  >
                    <Text style={styles.existingSessionPickerRowLabel}>
                      {session.session_number != null
                        ? `Session ${session.session_number}`
                        : `Session #${session.id}`}
                      {' · '}
                      {Number(session.exercise_count) === 1
                        ? '1 exercise'
                        : `${Number(session.exercise_count) || 0} exercises`}
                    </Text>
                    <Text style={styles.existingSessionPickerTapHint}>
                      Tap to select this session
                    </Text>
                    <View pointerEvents="none">
                      <TrainingSessionTimelineView
                        trainingSessionId={session.id}
                        studentId={currentUser?.id}
                        sessionNotes={session.notes}
                        durationMinutes={session.duration_minutes}
                        readOnly
                        initialExpanded
                        showNotes={false}
                        showSectionTitle={false}
                      />
                    </View>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={!!dateChangeClearModal}
        transparent
        animationType="fade"
        onRequestClose={() => setDateChangeClearModal(null)}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[styles.modalContent, styles.dateChangeClearModalContent]}
            {...(Platform.OS === 'web' ? { dataSet: { sessionDateChangeModal: 'true' } } : {})}
          >
            <Text style={styles.modalTitle}>Change training date</Text>
            <Text style={styles.dateChangeClearModalMessage}>
              Do you want to clear the exercises already selected?
            </Text>
            <View style={styles.modalButtonContainer}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={handleConfirmDateChangeClear}
              >
                <Text style={styles.modalButtonText}>Clear</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton]}
                onPress={handleConfirmDateChangeKeep}
              >
                <Text style={styles.modalButtonText}>Keep</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={!!duplicateSessionModal}
        transparent
        animationType="fade"
        onRequestClose={() => setDuplicateSessionModal(null)}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[styles.modalContent, styles.duplicateSessionModalContent]}
            {...(Platform.OS === 'web' ? { dataSet: { sessionDuplicateModal: 'true' } } : {})}
          >
            <ScrollView
              style={styles.duplicateSessionModalScroll}
              contentContainerStyle={styles.duplicateSessionModalScrollContent}
              showsVerticalScrollIndicator
              nestedScrollEnabled
            >
              <TrainingSessionTimelineView
                trainingSessionId={duplicateSessionModal?.session?.id}
                studentId={currentUser?.id}
                sessionNotes={duplicateSessionModal?.session?.notes}
                durationMinutes={duplicateSessionModal?.session?.duration_minutes}
                readOnly
                initialExpanded
                showNotes={false}
                showSectionTitle
              />
              <Text style={styles.duplicateSessionModalMessage}>
                {getDuplicateSessionPromptText(
                  duplicateSessionModal?.dateKey || sessionDate,
                  isUSUser
                )}
              </Text>
            </ScrollView>
            <View style={styles.modalButtonContainer}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setDuplicateSessionModal(null)}
                disabled={savingSession}
              >
                <Text style={styles.modalButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton, savingSession && styles.bottomButtonDisabled]}
                onPress={() => void handleConfirmDuplicateSessionSave()}
                disabled={savingSession}
              >
                <Text style={styles.modalButtonText}>Confirm</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Routine Name Modal */}
      <Modal
        visible={showRoutineModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => {
          setShowRoutineModal(false);
          setRoutineName('');
          setRoutineDescription('');
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => {
                setShowRoutineModal(false);
                setRoutineName('');
                setRoutineDescription('');
              }}
            >
              <Text style={styles.modalCloseButtonText}>✕</Text>
            </TouchableOpacity>

            <Text style={styles.modalTitle}>Name Routine</Text>

            {/* Routine Name Input */}
            <View style={styles.modalFieldContainer}>
              <Text style={styles.modalLabel}>Name Routine</Text>
              <TextInput
                style={styles.modalInput}
                value={routineName}
                onChangeText={setRoutineName}
                placeholder="Enter routine name"
                placeholderTextColor={theme.isDarkMode ? '#999' : '#666'}
              />
            </View>

            {/* Description Text Area */}
            <View style={styles.modalFieldContainer}>
              <Text style={styles.modalLabel}>Description</Text>
              <TextInput
                style={[styles.modalInput, styles.modalTextArea]}
                value={routineDescription}
                onChangeText={setRoutineDescription}
                placeholder="Enter description (optional)"
                placeholderTextColor={theme.isDarkMode ? '#999' : '#666'}
                multiline
                numberOfLines={4}
                textAlignVertical="top"
              />
            </View>

            {/* Save and Cancel Buttons */}
            <View style={styles.modalButtonContainer}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => {
                  setShowRoutineModal(false);
                  setRoutineName('');
                  setRoutineDescription('');
                }}
              >
                <Text style={styles.modalButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton]}
                onPress={handleSaveRoutine}
              >
                <Text style={styles.modalButtonText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    ...(Platform.OS === 'web' && !theme.isDarkMode
      ? { boxShadow: '0 1px 0 rgba(0,0,0,0.06)' }
      : {}),
  },
  hamburgerContainer: {
    marginRight: 15,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  breadcrumbContainer: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  breadcrumb: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.heading,
    /* RN Web: opaque bg emits r-backgroundColor → skips global black css-view rule behind title */
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  profileIconContainer: {
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  mainCalendarNav: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginBottom: 16,
    paddingVertical: 6,
    paddingHorizontal: 4,
    backgroundColor: 'transparent',
  },
  mainCalendarNavLabel: {
    marginLeft: 12,
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  content: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 40,
    flexGrow: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  timelineContainer: {
    marginBottom: 20,
    padding: 12,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#f9ffd6',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    ...(Platform.OS === 'web' && !theme.isDarkMode
      ? { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }
      : {}),
  },
  timelineSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 10,
  },
  timelineHintText: {
    fontSize: 13,
    color: theme.colors.text,
    opacity: 0.75,
    lineHeight: 18,
    marginBottom: 10,
  },
  timelineEmptyText: {
    fontSize: 14,
    color: theme.colors.text,
    opacity: 0.8,
    lineHeight: 20,
  },
  timelineItem: {
    marginBottom: 10,
  },
  exerciseTypeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  routineCheckboxContainer: {
    marginRight: 10,
    padding: 5,
    backgroundColor: 'transparent',
  },
  routineCheckbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: theme.colors.primary || '#d4af37',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.isDarkMode ? 'transparent' : theme.colors.surface,
  },
  routineCheckboxSelected: {
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  nameRoutineButton: {
    backgroundColor: theme.colors.primary || '#d4af37',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  nameRoutineButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  timelineActionButtons: {
    marginTop: 15,
    marginBottom: 10,
    gap: 8,
    backgroundColor: 'transparent',
  },
  removeTimelineButton: {
    backgroundColor: theme.isDarkMode ? '#8b2942' : '#c62828',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  removeTimelineButtonDisabled: {
    opacity: 0.65,
  },
  removeTimelineButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  exerciseTypeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
    padding: 10,
    backgroundColor: theme.isDarkMode ? theme.colors.background : theme.colors.surface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    ...(Platform.OS === 'web' && !theme.isDarkMode
      ? { overflow: 'hidden', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)' }
      : {}),
  },
  exerciseTypeIconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    backgroundColor: 'transparent',
  },
  exerciseTypeIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: theme.colors.primary || '#d4af37',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  exerciseTypeIconText: {
    color: '#FFFFFF',
    fontWeight: 'bold',
    fontSize: 14,
  },
  exerciseTypeName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    flex: 1,
    backgroundColor: 'transparent',
  },
  expandArrow: {
    fontSize: 16,
    color: theme.colors.text,
    marginLeft: 10,
    backgroundColor: 'transparent',
  },
  exercisesDropdown: {
    marginTop: 5,
    marginLeft: 20,
    paddingLeft: 20,
    borderLeftWidth: 2,
    borderLeftColor: theme.colors.primary || '#d4af37',
  },
  timelineExerciseItem: {
    padding: 8,
    marginBottom: 8,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F0F0F0',
    borderRadius: 6,
    borderWidth: theme.isDarkMode ? 0 : 1,
    borderColor: theme.isDarkMode ? 'transparent' : '#E8E8E8',
  },
  timelineExerciseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  timelineExerciseHeaderMain: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  timelineExerciseExpandBtn: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: 'transparent',
  },
  timelineExerciseExpandArrow: {
    fontSize: 14,
    color: theme.colors.heading || theme.colors.text,
    fontWeight: '600',
    backgroundColor: 'transparent',
  },
  timelineExerciseRemoveBtn: {
    paddingHorizontal: 8,
    paddingVertical: 8,
    backgroundColor: 'transparent',
  },
  timelineExerciseIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.primary || '#d4af37',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  timelineExerciseIconText: {
    color: '#FFFFFF',
    fontWeight: 'bold',
    fontSize: 12,
  },
  timelineExerciseInfo: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  timelineExerciseName: {
    fontSize: 14,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  timelineExerciseDetails: {
    fontSize: 12,
    color: theme.colors.text,
    opacity: 0.7,
    marginTop: 2,
    backgroundColor: 'transparent',
  },
  mainArea: {
    width: width * 0.8,
    alignSelf: 'center',
    paddingBottom: 20,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  mainAreaCarousel: {
    width: '100%',
    maxWidth: width,
    paddingHorizontal: 4,
    alignSelf: 'stretch',
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  sessionTilesCarouselRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    width: '100%',
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  sessionTilesCarouselScroll: {
    flex: 1,
    alignSelf: 'stretch',
    flexGrow: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  sessionTilesCarouselScrollInner: {
    flexDirection: 'row',
    alignItems: 'stretch',
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  carouselViewport: {
    flex: 1,
    justifyContent: 'center',
    alignSelf: 'stretch',
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  carouselSlide: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  carouselArrowButton: {
    width: 56,
    alignSelf: 'center',
    minHeight: 56,
    borderRadius: 28,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 2,
    ...Platform.select({
      web: { cursor: 'pointer' },
      default: {},
    }),
  },
  carouselArrowButtonDisabled: {
    opacity: 0.35,
  },
  carouselArrowLabel: {
    color: theme.isDarkMode ? '#FFFFFF' : '#000000',
    fontSize: 56,
    fontWeight: '200',
    lineHeight: 60,
    marginTop: -4,
    textAlign: 'center',
    includeFontPadding: false,
    ...(theme.isDarkMode
      ? Platform.select({
          web: {
            textShadow: '0 0 6px rgba(0,0,0,0.9), 0 0 14px rgba(0,0,0,0.75)',
          },
          default: {
            textShadowColor: 'rgba(0,0,0,0.85)',
            textShadowOffset: { width: 0, height: 0 },
            textShadowRadius: 8,
          },
        })
      : Platform.select({
          web: { textShadow: 'none' },
          default: {
            textShadowColor: 'transparent',
            textShadowOffset: { width: 0, height: 0 },
            textShadowRadius: 0,
          },
        })),
  },
  tilesRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 15,
  },
  coursesTile: {
    flex: 1,
    minWidth: 0,
    minHeight: 220,
    flexDirection: 'column',
    alignItems: 'stretch',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 8,
    paddingTop: 0,
    paddingHorizontal: 0,
    paddingBottom: 0,
    borderWidth: theme.isDarkMode ? 2 : 3,
    borderColor: theme.isDarkMode ? theme.colors.border : '#d4af37',
    ...(Platform.OS === 'web' && !theme.isDarkMode ? { overflow: 'hidden' } : {}),
    ...(Platform.OS === 'web' ? {
      boxShadow: theme.isDarkMode ? 'none' : '0 2px 8px rgba(212, 175, 55, 0.3)',
    } : {
      shadowColor: theme.isDarkMode ? 'transparent' : '#d4af37',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: theme.isDarkMode ? 0 : 0.3,
      shadowRadius: 4,
      elevation: theme.isDarkMode ? 0 : 4,
    }),
  },
  coursesTileCarouselSlide: {
    flex: 0,
    flexGrow: 0,
    width: '100%',
    maxWidth: 420,
    minHeight: 300,
    alignSelf: 'center',
  },
  coursesTileContentShell: {
    flex: 1,
    width: '100%',
    flexDirection: 'column',
    alignItems: 'stretch',
    paddingTop: 10,
    paddingHorizontal: 10,
    paddingBottom: 12,
    borderRadius: 0,
    overflow: 'hidden',
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  coursesTileImageWrap: {
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 96,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  coursesTileImageWrapCarousel: {
    minHeight: 150,
  },
  coursesTileImage: {
    width: '100%',
    height: 96,
  },
  coursesTileImageCarousel: {
    width: '100%',
    height: 168,
    maxHeight: 200,
  },
  placeholderImageContainer: {
    width: '100%',
    height: 80,
    marginBottom: 10,
    backgroundColor: theme.isDarkMode ? '#333' : '#E0E0E0',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  placeholderImageText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.isDarkMode ? '#999' : '#666',
    marginBottom: 4,
  },
  placeholderImageSubtext: {
    fontSize: 10,
    color: theme.isDarkMode ? '#666' : '#999',
  },
  coursesTileButtonSlot: {
    width: '100%',
    marginTop: 'auto',
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    borderRadius: 8,
    overflow: 'hidden',
  },
  coursesTileButton: {
    width: '100%',
    backgroundColor: '#d4af37',
    paddingVertical: 12,
    paddingHorizontal: 10,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    minHeight: 48,
  },
  coursesTileButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
    width: '100%',
    flexShrink: 1,
    /* RN Web: backgrounds emit r-backgroundColor → exempt from global black css-view rule */
    backgroundColor: '#d4af37',
    ...(Platform.OS === 'web' && !theme.isDarkMode ? { textShadow: 'none' } : {}),
  },
  button: {
    backgroundColor: theme.colors.primary || '#d4af37',
    padding: 15,
    borderRadius: 8,
    marginBottom: 10,
    alignItems: 'center',
  },
  exercisesButton: {
    marginTop: 20,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  dropdown: {
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 8,
    marginBottom: 10,
    padding: 10,
    marginTop: 10,
    width: width * 0.8,
    alignSelf: 'center',
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#d4af37',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    } : {
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.25,
      shadowRadius: 4,
      elevation: 5,
    }),
  },
  dropdownTitle: {
    color: theme.colors.text,
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 10,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: 'transparent',
    backgroundColor: 'transparent',
  },
  dropdownItem: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.isDarkMode ? '#333' : '#DDDDDD',
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  dropdownItemText: {
    color: theme.colors.text,
    fontSize: 16,
    backgroundColor: 'transparent',
  },
  dropdownSectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: theme.colors.heading || theme.colors.text,
    paddingHorizontal: 15,
    paddingTop: 12,
    paddingBottom: 8,
    backgroundColor: 'transparent',
  },
  sharedRoutineDropdownCard: {
    marginHorizontal: 10,
    marginBottom: 10,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fffef5',
  },
  sharedRoutineDropdownTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
    backgroundColor: 'transparent',
  },
  sharedRoutineDropdownBadge: {
    fontSize: 14,
    fontWeight: '700',
    color: theme.colors.primary || '#d4af37',
    backgroundColor: 'transparent',
  },
  sharedRoutineDropdownMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    backgroundColor: 'transparent',
  },
  sharedRoutineDropdownName: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  sharedRoutineDropdownOwner: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    opacity: 0.85,
    backgroundColor: 'transparent',
  },
  dropdownItemDisabled: {
    opacity: 0.45,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#F5F5F5',
  },
  dropdownItemTextDisabled: {
    color: theme.isDarkMode ? '#888' : '#999',
    backgroundColor: 'transparent',
  },
  gradePickerLabel: {
    color: theme.colors.text,
    fontSize: 14,
    fontWeight: '600',
    marginTop: 8,
    marginBottom: 6,
    backgroundColor: 'transparent',
  },
  gradePickerTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    marginBottom: 8,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
  },
  gradePickerTriggerText: {
    color: theme.colors.text,
    fontSize: 15,
    flex: 1,
    backgroundColor: 'transparent',
  },
  gradePickerDropdown: {
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    marginBottom: 8,
    overflow: 'hidden',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
  },
  gradePickerHint: {
    color: theme.isDarkMode ? '#aaa' : '#666',
    fontSize: 13,
    marginBottom: 8,
    backgroundColor: 'transparent',
  },
  dropdownItemSubtext: {
    color: theme.isDarkMode ? '#999' : '#666',
    fontSize: 12,
    marginTop: 4,
    fontStyle: 'italic',
    backgroundColor: 'transparent',
  },
  newButton: {
    padding: 15,
    alignItems: 'center',
    marginTop: 10,
  },
  newButtonText: {
    color: theme.colors.primary || '#d4af37',
    fontSize: 16,
    fontWeight: '600',
  },
  exerciseTile: {
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    padding: 15,
    borderRadius: 8,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: theme.colors.border,
    position: 'relative',
  },
  exerciseTileSelected: {
    backgroundColor: theme.isDarkMode ? '#000000' : '#FFF4D6',
    borderColor: theme.isDarkMode ? theme.colors.border : '#d4af37',
  },
  exerciseTileInner: {
    flex: 1,
    alignSelf: 'stretch',
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  exerciseTileInnerSelected: {
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFF4D6',
  },
  exerciseContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: 1,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  exerciseIconWithName: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  exerciseIconInDropdown: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.primary || '#d4af37',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  exerciseIconInDropdownText: {
    color: '#FFFFFF',
    fontWeight: 'bold',
    fontSize: 12,
  },
  exerciseInfo: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  exerciseName: {
    color: theme.colors.text,
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 5,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  exerciseDetails: {
    color: theme.colors.text,
    fontSize: 14,
    opacity: 0.7,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  selectAllContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingBottom: 10,
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 5, // Add padding for easier tapping
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: theme.isDarkMode ? '#FFFFFF' : '#333333',
    borderRadius: 4,
    marginRight: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  checkboxSelected: {
    backgroundColor: '#d4af37',
    borderColor: '#d4af37',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  selectAllText: {
    color: theme.colors.text,
    fontSize: 16,
    fontWeight: '600',
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  yellowOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#d4af37',
    opacity: 0.5,
    borderRadius: 8,
  },
  exerciseNameSelected: {
    color: theme.isDarkMode ? '#FFFFFF' : '#111111',
  },
  exerciseDetailsSelected: {
    color: theme.isDarkMode ? '#FFFFFF' : '#333333',
    opacity: theme.isDarkMode ? 0.9 : 1,
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 10,
    paddingVertical: 14,
    paddingHorizontal: 8,
    borderRadius: 8,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  actionButton: {
    padding: 12,
    borderRadius: 8,
    marginHorizontal: 10,
    minWidth: 100,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: '#999',
  },
  addButton: {
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  actionButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  menuHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  menuHeaderText: {
    color: theme.colors.text,
    fontSize: 16,
    fontWeight: '600',
  },
  menuArrow: {
    color: theme.colors.text,
    fontSize: 14,
  },
  subMenu: {
    paddingLeft: 20,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  goalRowWrapper: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  goalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 10,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  goalRowExpanded: {
    backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#F5F5F5',
  },
  goalRowMain: {
    flex: 1,
    marginRight: 12,
  },
  goalRowTitle: {
    color: theme.colors.text,
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 4,
  },
  goalRowSubtext: {
    color: theme.colors.text,
    opacity: 0.75,
    fontSize: 13,
  },
  goalSelectButton: {
    backgroundColor: theme.colors.primary || '#d4af37',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
    minWidth: 72,
    alignItems: 'center',
  },
  goalSelectButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700',
  },
  goalMilestonesPanel: {
    paddingHorizontal: 12,
    paddingBottom: 12,
    paddingTop: 6,
    backgroundColor: theme.isDarkMode ? '#111111' : '#FFFFFF',
    borderTopWidth: theme.isDarkMode ? 0 : StyleSheet.hairlineWidth,
    borderTopColor: theme.colors.border,
  },
  goalMilestoneText: {
    color: theme.colors.text,
    fontSize: 13,
    marginBottom: 5,
  },
  advancedSection: {
    marginTop: 20,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#f9ffd6',
    overflow: 'hidden',
    ...(theme.isDarkMode
      ? {
          borderTopWidth: 1,
          borderTopColor: theme.colors.border,
        }
      : {
          borderWidth: 1,
          borderColor: theme.colors.border,
          borderRadius: 8,
          ...(Platform.OS === 'web'
            ? { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }
            : {}),
        }),
  },
  advancedHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    backgroundColor: 'transparent',
  },
  advancedArrow: {
    color: theme.colors.text,
    fontSize: 14,
    marginRight: 10,
  },
  advancedTitle: {
    color: theme.colors.text,
    fontSize: 16,
    fontWeight: '600',
  },
  advancedForm: {
    padding: 15,
    paddingTop: 0,
    backgroundColor: 'transparent',
  },
  existingSessionHint: {
    fontSize: 13,
    color: theme.colors.text,
    opacity: 0.75,
    marginTop: 8,
    marginBottom: 4,
  },
  existingSessionPromptBlock: {
    marginBottom: 12,
    paddingHorizontal: 4,
  },
  existingSessionCheckboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 12,
    marginBottom: 4,
    paddingVertical: 8,
  },
  existingSessionCheckboxPressable: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  existingSessionInfoButton: {
    padding: 4,
    marginLeft: 4,
    marginTop: 1,
  },
  existingSessionHelpBox: {
    marginTop: 4,
    marginBottom: 8,
    marginLeft: 30,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#faf8f4',
  },
  existingSessionHelpText: {
    fontSize: 13,
    lineHeight: 18,
    color: theme.colors.text,
  },
  existingSessionCheckboxMark: {
    fontSize: 20,
    marginRight: 10,
    color: theme.colors.heading || theme.colors.primary || '#d4af37',
    lineHeight: 22,
  },
  existingSessionCheckboxLabel: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    color: theme.colors.text,
    fontWeight: '500',
  },
  label: {
    color: theme.colors.text,
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
    marginTop: 10,
  },
  textArea: {
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    color: theme.colors.text,
    minHeight: 100,
    textAlignVertical: 'top',
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  input: {
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    color: theme.colors.text,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  sessionDateInputWrap: {
    position: 'relative',
    width: '100%',
  },
  sessionDateInput: {
    paddingRight: 44,
    width: '100%',
  },
  sessionDateCalendarIconButton: {
    position: 'absolute',
    right: 8,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    width: 36,
    paddingHorizontal: 4,
    backgroundColor: 'transparent',
  },
  bottomButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 30,
    paddingBottom: 12,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  bottomButton: {
    flex: 1,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginHorizontal: 5,
  },
  bottomButtonDisabled: {
    opacity: 0.65,
  },
  saveButton: {
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  saveButtonDisabled: {
    backgroundColor: theme.isDarkMode ? '#4a4a4a' : '#bdbdbd',
  },
  saveButtonTextDisabled: {
    color: theme.isDarkMode ? '#888888' : '#757575',
  },
  bottomButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  sessionSaveToast: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginHorizontal: 5,
    marginBottom: 18,
  },
  sessionSaveToastError: {
    backgroundColor: '#c62828',
  },
  sessionSaveToastSuccess: {
    backgroundColor: '#2e7d32',
  },
  sessionSaveToastMessage: {
    flex: 1,
    color: '#ffffff',
    fontSize: 15,
    lineHeight: 20,
    marginRight: 8,
    paddingRight: 4,
    fontWeight: '700',
    backgroundColor: 'transparent',
  },
  sessionSaveToastClose: {
    padding: 4,
    marginTop: -2,
    backgroundColor: 'transparent',
  },
  sessionSaveToastCloseText: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: '700',
    backgroundColor: 'transparent',
  },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: width * 0.9,
    maxHeight: '80%',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    position: 'relative',
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
    } : {
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 4,
      },
      shadowOpacity: 0.5,
      shadowRadius: 10,
      elevation: 10,
    }),
  },
  modalCloseButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  modalCloseButtonText: {
    color: theme.colors.text,
    fontSize: 20,
    fontWeight: 'bold',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: 20,
    textAlign: 'center',
  },
  modalFieldContainer: {
    marginBottom: 15,
    width: '100%',
  },
  modalLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 8,
  },
  modalInput: {
    borderWidth: 1,
    borderColor: theme.isDarkMode ? '#444' : '#DDD',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: theme.colors.text,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    width: '100%',
  },
  modalTextArea: {
    minHeight: 100,
    paddingTop: 12,
  },
  modalButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    gap: 10,
  },
  modalButton: {
    flex: 1,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  duplicateSessionModalContent: {
    maxHeight: '85%',
    paddingBottom: 16,
  },
  duplicateSessionModalScroll: {
    maxHeight: '100%',
    flexGrow: 0,
  },
  duplicateSessionModalScrollContent: {
    paddingBottom: 8,
  },
  duplicateSessionModalMessage: {
    fontSize: 15,
    lineHeight: 22,
    color: theme.colors.text,
    textAlign: 'center',
    marginTop: 16,
    marginBottom: 4,
    fontWeight: '500',
  },
  dateChangeClearModalContent: {
    maxWidth: 420,
  },
  dateChangeClearModalMessage: {
    fontSize: 15,
    lineHeight: 22,
    color: theme.colors.text,
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 4,
    fontWeight: '500',
    backgroundColor: 'transparent',
  },
  existingSessionPickerModalContent: {
    maxHeight: '88%',
    paddingTop: 44,
  },
  existingSessionPickerSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    color: theme.colors.text,
    opacity: 0.85,
    textAlign: 'center',
    marginBottom: 16,
    paddingHorizontal: 4,
  },
  existingSessionPickerScroll: {
    maxHeight: '100%',
    flexGrow: 0,
  },
  existingSessionPickerScrollContent: {
    paddingBottom: 8,
  },
  existingSessionPickerRow: {
    borderWidth: 2,
    borderColor: theme.isDarkMode ? '#444444' : '#dddddd',
    borderRadius: 10,
    padding: 10,
    marginBottom: 14,
    backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#f8f8f8',
  },
  existingSessionPickerRowSelected: {
    borderColor: '#fdfd0a',
    backgroundColor: theme.isDarkMode ? 'rgba(253, 253, 10, 0.14)' : 'rgba(253, 253, 10, 0.28)',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 0 0 2px rgba(253, 253, 10, 0.45)' }
      : {}),
  },
  existingSessionPickerTapHint: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.heading || '#d4af37',
    marginBottom: 8,
    opacity: 0.9,
    backgroundColor: 'transparent',
  },
  existingSessionPickerRowLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: theme.colors.heading || '#d4af37',
    marginBottom: 8,
  },
  modalExerciseName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.heading || '#d4af37',
    marginBottom: 15,
    textAlign: 'center',
  },
  modalMediaContainer: {
    width: '100%',
    alignItems: 'center',
    marginBottom: 15,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
  },
  modalExerciseCarouselBelowVideo: {
    marginTop: 14,
    width: '100%',
    alignSelf: 'stretch',
  },
  modalExerciseCarouselTransformWrap: {
    backgroundColor: 'transparent',
    alignSelf: 'center',
  },
  modalExerciseDetailLower: {
    width: '100%',
    alignSelf: 'stretch',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
  },
  modalExerciseGalleryRotateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginBottom: 12,
    gap: 24,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
  },
  modalExerciseGalleryRotateHit: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  modalImageRotateWrap: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  modalImage: {
    width: '100%',
    height: 250,
    borderRadius: 8,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
  },
  youtubeContainer: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#000',
  },
  youtubeIframe: {
    width: '100%',
    height: '100%',
  },
  youtubeLinkText: {
    color: theme.colors.text,
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 10,
  },
  youtubeNoteText: {
    color: theme.colors.text,
    fontSize: 12,
    textAlign: 'center',
    fontStyle: 'italic',
    opacity: 0.7,
  },
  modalDescriptionContainer: {
    marginTop: 4,
    marginBottom: 10,
    width: '100%',
    alignSelf: 'stretch',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
  },
  modalDescriptionLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 8,
  },
  modalDescriptionText: {
    fontSize: 14,
    color: theme.colors.text,
    lineHeight: 20,
    textAlign: 'left',
  },
  modalExerciseDetails: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
    width: '100%',
    alignSelf: 'stretch',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
  },
  modalExerciseDetailsText: {
    fontSize: 14,
    color: theme.colors.text,
    textAlign: 'center',
    fontWeight: '500',
    backgroundColor: 'transparent',
  },
  exerciseImageLightboxRoot: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.92)',
    overflow: 'visible',
  },
  exerciseImageLightboxSheet: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    paddingTop: 48,
    paddingBottom: 12,
    paddingHorizontal: 10,
    overflow: 'visible',
  },
  exerciseImageLightboxRotateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    gap: 24,
    paddingVertical: 8,
    backgroundColor: 'transparent',
  },
  exerciseImageLightboxClose: {
    position: 'absolute',
    top: 4,
    right: 4,
    zIndex: 10,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.22)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  exerciseImageLightboxCloseText: {
    color: '#fff',
    fontSize: 22,
    fontWeight: '700',
  },
  exerciseImageLightboxStage: {
    flex: 1,
    width: '100%',
    minHeight: 0,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
  exerciseImageLightboxImage: {
    borderRadius: 10,
    backgroundColor: 'transparent',
  },
  exerciseCarouselVideoModalRoot: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.88)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 14,
  },
  exerciseCarouselVideoModalSheet: {
    width: Math.min(width * 0.92, 880),
    height: Math.min(Math.round(width * 0.56), 440),
    backgroundColor: '#111',
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#333',
    zIndex: 2,
    flexDirection: 'column',
  },
  exerciseCarouselVideoFrame: {
    flex: 1,
    width: '100%',
    paddingTop: 36,
  },
  exerciseCarouselVideoModalClose: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 10,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: 'rgba(0,0,0,0.55)',
    borderRadius: 16,
  },
  exerciseCarouselVideoModalCloseText: {
    color: '#fff',
    fontSize: 17,
    fontWeight: '700',
  },
  exerciseCarouselVideoWebView: {
    flex: 1,
    width: '100%',
    backgroundColor: '#000',
    paddingTop: 36,
  },
});
